// Reports (§5 /reports) — landing grid + report views. Live-parity port of v2's report suite:
// shared quick-date presets, TB/BS FY steppers + summary cards, the ruled Income Statement
// (Sales→COS→Gross Profit→Other Income→Expenses→Tax→Net), the two-column Balance Sheet with
// group subtotals + control badges, Age Analysis with status filter + click-to-filter buckets,
// General Ledger single-account AND account-range (grouped, BBF) modes, VAT groups + tabs,
// Bank Statement cards, 3-pass Reconciliation tabs and the Cost Code dr/cr split.
// Declared deviations: PDF/Excel exports are client CSV + print for now; BS codes don't
// deep-link to the GL report (the hash router carries no query params); no FA-code column
// (no fixed-assets module yet).
import { html, signal, computed, navigate } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { dropdownKeydown } from '../shared/dropnav.js';
import { sortTh, nextSort, sortRows } from '../shared/sortcols.js';
import { tenantShell, noteUrlChange } from './tshell.js';
import { paneFx } from '../shared/pane.js';
import { fmtMoney, formatDate, formatGlCode, controlTag, totalTone, isForeignAccount, baseCurrencyCode } from './bank_helpers.js';
import { mountFloatRoot, closeFloatRoot } from '../shared/floatroot.js';
import { configureSleekDate } from '../shared/sleekdate.js';
import { t } from '../shared/i18n.js';
import { icon } from '../shared/icons.js';
import { backChip, setScreenLabel } from '../shared/vtnav.js';
import { notice } from '../shared/notice.js';
import { statementViewer } from '../shared/statementview.js';
import { documentViewer } from '../shared/docview.js';

// Static IA (mirrors reporting_service.report_catalogue). filter drives the view's controls.
// `t` = the name's word key (admin Words / reference.translation); `name` = its English fallback.
// (REPORTS is evaluated at module load — BEFORE the i18n bundle lands — so we store the KEY here
// and call t(r.t, r.name) at render time, never t() in this literal.)
const REPORTS = [
  { code: 'trial-balance',    name: 'Trial Balance',       group: 'Financial', filter: 'fy-asat', desc: "Every account's balance as at a date, debit vs credit.", t: 'reports.trial_balance', dt: 'reports.trial_balance_desc' },
  { code: 'income-statement', name: 'Income Statement',    group: 'Financial', filter: 'range',   desc: 'Income less expenses = profit or loss for a period.', t: 'reports.income_statement', dt: 'reports.income_statement_desc' },
  { code: 'balance-sheet',    name: 'Balance Sheet',       group: 'Financial', filter: 'fy',      desc: 'Assets, liabilities and equity as at a date.', t: 'reports.balance_sheet', dt: 'reports.balance_sheet_desc' },
  { code: 'age-analysis',     name: 'Age Analysis',        group: 'Debtors & Creditors', filter: 'party', desc: 'Outstanding debtor / creditor balances by age.', t: 'report.age.title', dt: 'reports.age_analysis_desc' },
  { code: 'general-ledger',   name: 'General Ledger',      group: 'Detail',    filter: 'gl',      desc: 'Transactions on one account — or a whole account range.', t: 'reports.general_ledger', dt: 'reports.general_ledger_desc' },
  { code: 'transactions',     name: 'Transaction Listing', group: 'Detail',    filter: 'search',  desc: 'Search all posted ledger movements.', t: 'reports.transactions', dt: 'reports.transactions_desc' },
  { code: 'accounts',         name: 'List of Accounts',    group: 'Detail',    filter: 'none',    desc: 'The chart of accounts with groups, tax codes and types.', t: 'reports.list_of_accounts', dt: 'reports.list_of_accounts_desc' },
  { code: 'vat',              name: 'VAT Report',          group: 'Tax',       filter: 'range',   desc: 'VAT by tax code for a period (VAT201).', t: 'reports.vat_report', dt: 'reports.vat_report_desc' },
  { code: 'bank-statement',   name: 'Bank Statement',      group: 'Detail',    filter: 'account', desc: "A cashbook's movements with a running balance.", t: 'reports.bank_statement', dt: 'reports.bank_statement_desc' },
  { code: 'reconciliation',   name: 'Bank Reconciliation', group: 'Detail',    filter: 'account', desc: 'Bank import versus ledger, matched and unmatched.', t: 'reports.reconciliation', dt: 'reports.reconciliation_desc' },
  { code: 'cost-codes',       name: 'Cost Code Report',    group: 'Detail',    filter: 'costcode', desc: 'Ledger movements grouped by cost centre.', t: 'report.cc.title', dt: 'reports.cost_codes_desc' },
];
// FY arithmetic — mirrors the server's src/app/fy.py: the financial year is a DAY +
// MONTH start ("starts 15 March"); fy labels the calendar year the FY ENDS in.
const isoD = d => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0')
  + '-' + String(d.getDate()).padStart(2, '0');
const clampDay = (y, m, d) => new Date(y, m - 1, Math.min(d, new Date(y, m, 0).getDate()));
export function fyBoundsForYear(fy, fyStart){
  const m = Number(fyStart && fyStart.month) || 3, d = Number(fyStart && fyStart.day) || 1;
  if(m === 1 && d === 1) return [new Date(fy, 0, 1), new Date(fy, 11, 31)];
  const end = new Date(clampDay(fy, m, d).getTime() - 86400000);
  return [clampDay(fy - 1, m, d), end];
}

// v2 quick-range presets (port of sleek-report-dates.js setQuickDate) — pure so it's
// testable. fyStart = {month, day} (the company's FY start); fy = the FY-end YEAR label.
export function quickRange(preset, fyStart, fy, today){
  const t = today || new Date(), y = t.getFullYear(), m = t.getMonth();
  const iso = (yy, mm, dd) => yy + '-' + String(mm).padStart(2, '0') + '-' + String(dd).padStart(2, '0');
  const daysIn = (yy, mm) => new Date(yy, mm, 0).getDate();
  if(preset === 'this_month') return [iso(y, m + 1, 1), iso(y, m + 1, daysIn(y, m + 1))];
  if(preset === 'last_month'){ const lm = new Date(y, m - 1, 1);
    return [iso(lm.getFullYear(), lm.getMonth() + 1, 1),
            iso(lm.getFullYear(), lm.getMonth() + 1, daysIn(lm.getFullYear(), lm.getMonth() + 1))]; }
  if(preset === 'bi_monthly'){ const a = new Date(y, m - 2, 1), b = new Date(y, m - 1, 1);
    return [iso(a.getFullYear(), a.getMonth() + 1, 1),
            iso(b.getFullYear(), b.getMonth() + 1, daysIn(b.getFullYear(), b.getMonth() + 1))]; }
  if(preset === 'this_fy'){ const [s, e] = fyBoundsForYear(fy, fyStart); return [isoD(s), isoD(e)]; }
  if(preset === 'last_fy'){ const [s, e] = fyBoundsForYear(fy - 1, fyStart); return [isoD(s), isoD(e)]; }
  if(preset === 'ytd'){ const [s] = fyBoundsForYear(fy, fyStart); return [isoD(s), iso(y, m + 1, t.getDate())]; }
  if(preset === 'all') return ['2000-01-01', iso(y, 12, 31)];
  return [null, null];
}

// The FY-end YEAR whose financial year contains `today` (mirrors the server's fy.fy_year_of).
export function currentFyYear(fyStart, today){
  const t = today || new Date();
  const m = Number(fyStart && fyStart.month) || 3, d = Number(fyStart && fyStart.day) || 1;
  let s = clampDay(t.getFullYear(), m, d);
  if(t < s) s = clampDay(t.getFullYear() - 1, m, d);
  return (m === 1 && d === 1) ? s.getFullYear() : s.getFullYear() + 1;
}

// The period a report's drill-down opens the General Ledger on — taken from the report's OWN data (the
// rows that were clicked), so the ledger's figures are the ones clicked (owner 2026-09-30: "so that the
// information is always relevant to the year"). A period report (Income Statement, movements Trial
// Balance) → its From–To. An as-at report (Trial Balance balances, Balance Sheet) → the first day of the
// financial year its as-at date falls in, to that date: the ledger then closes on the figure clicked.
// [] = no dates (the ledger's own default, this financial year).
export function drillPeriod(code, d, fyStart){
  if(!d) return [];
  if(code === 'accounts' || code === 'income-statement' || (code === 'trial-balance' && d.mode === 'movement'))
    return d.date_from && d.date_to ? [d.date_from, d.date_to] : [];
  if(code !== 'trial-balance' && code !== 'balance-sheet') return [];
  const to = String(d.as_at || '').slice(0, 10);
  const [y, m, day] = to.split('-').map(Number);
  if(!/^\d{4}-\d{2}-\d{2}$/.test(to) || !y || !m || !day) return [];
  const [start] = fyBoundsForYear(currentFyYear(fyStart, new Date(y, m - 1, day)), fyStart);
  return [isoD(start), to];
}

// ── Landing grid — v2 index.twig: ONE flat grid of centred icon cards, no group labels ──
// Icon names map to Lucide keys (see src/shared/icons.js) so every card renders as a 20×20
// stroked SVG in currentColor — consistent stroke weight and no more Unicode-glyph mismatch.
const RP_ICONS = { 'trial-balance': 'scale', 'income-statement': 'trending-up',
  'balance-sheet': 'book-open-check', 'age-analysis': 'clock', 'general-ledger': 'book-open',
  'transactions': 'arrow-up-down', 'accounts': 'list-ordered', 'vat': 'percent',
  'bank-statement': 'landmark', 'reconciliation': 'check-check', 'cost-codes': 'tag' };
// The VAT return's long dates, as the accountant's return reads them: "1 June 2026".
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December'];
export function vatLongDate(iso){
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if(!m || Number(m[2]) < 1 || Number(m[2]) > 12) return String(iso || '');
  const month = MONTHS_EN[Number(m[2]) - 1];
  return Number(m[3]) + ' ' + t('common.month_' + month.toLowerCase(), month) + ' ' + m[1];
}

// CSV of the VAT201 return: the fields, every transaction by field, then the lines not included.
export function vat201Csv(v){
  const line = (f, x) => [f, x.date, (x.account_code ? x.account_code + ' - ' : '') + (x.account_name || ''),
    x.reference, x.description, x.tax_rate, x.gross, x.net, x.tax, x.adjustment ? 'Adjustment' : ''];
  const head = ['SARS Reference', 'Date', 'Account', 'Reference', 'Description', 'Tax rate', 'Gross', 'Net', 'Tax', ''];
  return [['SARS Reference', 'Description', 'Amount'],
    ...(v.fields || []).map(f => [f.field, f.label, f.value]),     // the number alone, as on the screen
    [], ['Transactions by field number'], head,
    ...(v.sections || []).flatMap(s => s.rows.map(x => line(s.field, x))),
    [], ['Transactions not included'], head,
    ...(v.not_included || []).map(x => line('', x))];
}

export function reportsScreen(companyId){
  // the VAT flag has teeth: the VAT Report tile exists only for a VAT-registered
  // company (the server refuses the report + its exports either way)
  const vatReg = signal(true);
  (async () => {
    try { const r = await api.get('/companies/' + companyId + '/bank-reference');
          vatReg.value = !!r.vat_registered;
          // Feed the shared <sleek-date> its format + financial periods, so the VAT Report
          // (and every other) range picker shows dd/mm/yyyy and is period-aware — reports was
          // the one date screen that never configured it (owner 2026-09-19: "VAT returns screen
          // date inputs are messed up").
          configureSleekDate(r); } catch {}
  })();
  const grid = () => {
    const cards = REPORTS.filter(r => r.code !== 'vat' || vatReg.value);
    return html`<div class="rp-grid rp-grid-v2">${cards.map(r => html`
      <div class="rp-card rp-card-v2" @click=${() => navigate('/co/' + companyId + '/reports/' + r.code)}>
        <div class="rp-card-ico">${icon(RP_ICONS[r.code] || 'layout-dashboard', { size: 32 })}</div>
        <div class="rp-card-name">${t(r.t, r.name)}</div>
        <div class="rp-card-desc">${t(r.dt, r.desc)}</div>
      </div>`)}</div>`;
  };
  const content = html`<div class="page page-wide">
    <div class="page-head"><div><h1 class="page-title">${() => t('common.reports', 'Reports')}</h1></div></div>
    ${grid}
  </div>`;
  return tenantShell(companyId, t('common.reports', 'Reports'), content);
}

// ── Report view ─────────────────────────────────────────────────────────────
export function reportViewScreen(companyId, code){
  const meta = REPORTS.find(r => r.code === code) || { code, name: 'Report', filter: 'none' };
  // the ← trail names this screen by its report ("← General Ledger"), not by its section ("← Reports")
  setScreenLabel(location.pathname, t(meta.t, meta.name));
  const data = signal(null), loading = signal(true), err = signal('');
  const asAt = signal(''), dFrom = signal(''), dTo = signal('');
  const party = signal('0'), ageStatus = signal('active'), ageBucket = signal('');
  const accountId = signal(''), q = signal(''), ccId = signal('');
  const fyYear = signal(0);                 // 0 until the server resolves the FY
  const asAtMode = signal('fy');            // TB: 'fy' (year drives as-at) | 'date' (explicit as-at) | 'movement' (from–to activity only)
  const atRate = signal(false);             // TB: false = balances as booked; true = foreign bank rows revalued at the report-date rate (display only, no journal). Base-currency rows are unchanged either way.
  const glMode = signal('single');          // GL: 'single' | 'range'
  const vatTab = signal('all'), reconTab = signal('matched');   // VAT detail tabs: All | Documents | Entries
  // VAT Report view (South Africa): 'vat201' = the SARS VAT201 return the accountant files (owner
  // 2026-10-07) | 'codes' = the earlier by-tax-code view. The downloads follow the view shown.
  const vatView = signal('vat201');
  const accSort = signal({ key: 'code', dir: 'asc' });   // List of Accounts column sort (client-side)
  const txnSort = signal({ key: 'date', dir: 'desc' });  // Transaction Listing column sort (SERVER-side, sorts before the 500 cap)
  const accounts = signal([]);
  const costCentres = signal([]);           // Cost Code filter list — set only when it changes (see filterBar)
  let glFromCode = '', glToCode = '';       // range picker values (plain vars — no re-render while typing)
  let timer;

  // Drill-down: any grouped ledger summary (trial balance, income statement, balance sheet…) links
  // each account to its General Ledger detail for the same context (owner 2026-09-21: "grouped
  // ledger summaries should always be able to drill down to detail"). A ?account=<id> deep-link
  // seeds the GL report so it opens on that account.
  const _qp = (() => { try { return new URLSearchParams(location.search); } catch { return new URLSearchParams(); } })();
  if(code === 'general-ledger' && _qp.get('account')){ accountId.value = _qp.get('account'); glMode.value = 'single'; }
  // every drill carries its report's period (drillPeriod) so the GL shows the same year / window
  if(code === 'general-ledger'){ if(_qp.get('from')) dFrom.value = _qp.get('from'); if(_qp.get('to')) dTo.value = _qp.get('to'); }
  // a VAT Report line opens the ledger ON its entry (owner 2026-10-07): ?journal=<id> highlights that
  // journal's lines and scrolls the first into view once
  const glJournal = code === 'general-ledger' ? Number(_qp.get('journal')) || 0 : 0;
  let glScrolled = false;
  // The General Ledger and the VAT Report keep their place in their own URL — the period, the account (or
  // account range), the VAT view, and the line last opened — so ← from a document or from the ledger
  // reopens the same view with that line marked (owner 2026-10-08: "the back screens take the user back
  // to where they were"). syncPlaceUrl writes it; a click that leaves for a line writes that line first.
  if(code === 'general-ledger' && _qp.get('range') === '1'){
    glMode.value = 'range'; glFromCode = _qp.get('code_from') || ''; glToCode = _qp.get('code_to') || '';
  }
  if(code === 'vat'){
    if(_qp.get('from')) dFrom.value = _qp.get('from');
    if(_qp.get('to')) dTo.value = _qp.get('to');
    if(_qp.get('view') === 'codes') vatView.value = 'codes';
  }
  const vatFocus = code === 'vat' ? Number(_qp.get('focus')) || 0 : 0;
  let vatScrolled = false;
  let placeMark = glJournal || vatFocus;   // the line the URL marks (the last one opened)
  function syncPlaceUrl(mark){
    if(code !== 'general-ledger' && code !== 'vat') return;
    if(mark) placeMark = mark;
    try {
      const u = new URL(location.href);
      ['account', 'range', 'code_from', 'code_to', 'from', 'to', 'view', 'journal', 'focus'].forEach(k => u.searchParams.delete(k));
      if(code === 'general-ledger'){
        if(glMode.value === 'range'){
          u.searchParams.set('range', '1');
          if(glFromCode) u.searchParams.set('code_from', glFromCode);
          if(glToCode) u.searchParams.set('code_to', glToCode);
        } else if(accountId.value) u.searchParams.set('account', accountId.value);
      }
      if(dFrom.value) u.searchParams.set('from', dFrom.value);
      if(dTo.value) u.searchParams.set('to', dTo.value);
      if(code === 'vat' && vatView.value === 'codes') u.searchParams.set('view', 'codes');
      if(placeMark) u.searchParams.set(code === 'vat' ? 'focus' : 'journal', String(placeMark));
      // replaceState: updates the address without a new history entry or a router re-render
      history.replaceState(history.state, '', u.pathname + u.search);
      noteUrlChange(companyId);    // keep the ← trail + main-menu memory on the CURRENT place
    } catch { /* URL sync is a convenience — never block the report */ }
  }
  // The TB keeps ALL its settings in its own URL — mode (Balances / Movements), the From–To window,
  // FY year, explicit as-at date and the at-report-date-rate toggle — so returning to Reports from
  // another main menu (tshell's place memory), ←, a refresh or a shared link reopens the exact view
  // (owner 2026-09-24: "still on TB with all the settings I had before").
  const TB_KEYS = ['mode', 'from', 'to', 'year', 'as_at', 'rate'];
  if(code === 'trial-balance'){
    if(_qp.get('mode') === 'movement' && _qp.get('from') && _qp.get('to')){
      asAtMode.value = 'movement'; dFrom.value = _qp.get('from'); dTo.value = _qp.get('to');
    } else if(_qp.get('as_at')){
      asAtMode.value = 'date'; asAt.value = _qp.get('as_at');
    }
    if(Number(_qp.get('year'))) fyYear.value = Number(_qp.get('year'));
    if(_qp.get('rate') === '1') atRate.value = true;
  }
  function syncTbUrl(){
    if(code !== 'trial-balance') return;
    try {
      const u = new URL(location.href);
      TB_KEYS.forEach(k => u.searchParams.delete(k));
      if(asAtMode.value === 'movement' && dFrom.value && dTo.value){
        u.searchParams.set('mode', 'movement'); u.searchParams.set('from', dFrom.value); u.searchParams.set('to', dTo.value);
      } else {
        if(fyYear.value) u.searchParams.set('year', String(fyYear.value));
        if(asAtMode.value === 'date' && asAt.value) u.searchParams.set('as_at', asAt.value);
      }
      if(atRate.value) u.searchParams.set('rate', '1');
      // replaceState: updates the address without a new history entry or a router re-render
      history.replaceState(history.state, '', u.pathname + u.search);
      noteUrlChange(companyId);    // keep the ← trail + main-menu memory on the CURRENT settings
    } catch { /* URL sync is a convenience — never block the report */ }
  }
  const glHref = acctId => {
    if(!acctId) return '';
    let url = '/co/' + companyId + '/reports/general-ledger?account=' + acctId;
    // the report's own period (drillPeriod): the ledger opens on the year / dates the rows came from
    const [from, to] = drillPeriod(code, data.value, fyStart());
    if(from && to) url += '&from=' + from + '&to=' + to;
    return url;
  };
  const glDrill = acctId => { const url = glHref(acctId); if(url) navigate(url); };

  const co = () => (data.value && data.value.company) || {};
  const df = () => (co().date_format || co().country_date_format || 'dd/MM/yyyy');
  const dmy = iso => formatDate(iso, df());
  const gcode = c => formatGlCode(c);
  // every figure renders in the COMPANY's base currency (R / $ / € — from the payload)
  const fmtR = v => fmtMoney(v, co().currency_symbol, false);
  const fmtRS = v => fmtMoney(v, co().currency_symbol, true);
  const fyStart = () => ({ month: Number(co().fy_start_month) || 3,
                           day: Number(co().fy_start_day) || 1 });

  const isRange = ['range', 'search', 'account', 'gl', 'costcode'].includes(meta.filter);

  function query(){
    const p = new URLSearchParams();
    if(meta.filter === 'fy-asat'){
      // TB movements mode: only the activity between two dates, no opening balances (owner 2026-09-24)
      if(asAtMode.value === 'movement'){ if(dFrom.value) p.set('from', dFrom.value); if(dTo.value) p.set('to', dTo.value); }
      else if(asAtMode.value === 'date' && asAt.value) p.set('as_at', asAt.value);
      else if(fyYear.value) p.set('year', String(fyYear.value));
    }
    if(meta.filter === 'fy' && fyYear.value) p.set('year', String(fyYear.value));
    if(meta.filter === 'party'){ p.set('type', party.value); p.set('status', ageStatus.value); }
    if(meta.filter === 'gl'){
      if(glMode.value === 'range'){ if(glFromCode) p.set('code_from', glFromCode); if(glToCode) p.set('code_to', glToCode);
        if(!glFromCode && !glToCode) p.set('code_from', '0'); }
      else if(accountId.value) p.set('account_id', accountId.value);
    }
    if(meta.filter === 'account' && accountId.value) p.set('account_id', accountId.value);
    if(meta.filter === 'search' && q.value.trim()) p.set('q', q.value.trim());
    if(meta.filter === 'costcode' && ccId.value) p.set('cost_centre_id', ccId.value);
    if(code === 'transactions'){ p.set('sort', txnSort.value.key); p.set('dir', txnSort.value.dir); }
    if(isRange){ if(dFrom.value) p.set('from', dFrom.value); if(dTo.value) p.set('to', dTo.value); }
    if(code === 'vat' && vatView.value === 'codes') p.set('view', 'codes');
    return p.toString();
  }

  // pane-in on the report body: on open (first data) and on the in-report tab bars —
  // never on ordinary filter/search reloads (same convention as contacts / cashbook).
  const pane = paneFx();
  let firstLoad = true;
  // Each key that changes a date reloads the report, so replies overlap. Only the newest reload may land: the
  // reload for a first key (no date yet, the server's default period) answering after the one for the
  // finished date put the default period's figures under the typed date (2026-10-09).
  let loadSeq = 0;
  // Reconciliation: the bank side is in the bank ACCOUNT's own currency, the ledger side in the
  // company's base (owner 2026-10-19: no currency bleeds) — the account cards say which currency.
  let bankCards = null;
  const reconCard = d => ((bankCards || []).find(c => d && d.account && Number(c.gl_account_id) === Number(d.account.id))) || null;
  const reconSym = d => { const c = reconCard(d) || {}; return c.own_currency_symbol || c.currency_symbol || c.own_currency_code || c.currency_code || ''; };
  const reconForeign = d => isForeignAccount(reconCard(d) || {}, { code: co().currency_code || baseCurrencyCode() });

  async function load(){
    const seq = ++loadSeq;
    loading.value = true;
    syncTbUrl();
    try {
      const r = await api.get('/companies/' + companyId + '/reports/' + code + (query() ? '?' + query() : ''));
      if(seq !== loadSeq) return;
      if(code === 'reconciliation' && r && r.account && !bankCards){
        try { bankCards = (await api.get('/companies/' + companyId + '/bank-accounts')).data || []; }
        catch { bankCards = null; }
        if(seq !== loadSeq) return;
      }
      data.value = r; err.value = '';
      if(r.accounts && !accounts.value.length) accounts.value = r.accounts;
      if(r.cost_centres && JSON.stringify(r.cost_centres) !== JSON.stringify(costCentres.value)) costCentres.value = r.cost_centres;
      if(r.date_from && !dFrom.value) dFrom.value = r.date_from;
      if(r.date_to && !dTo.value) dTo.value = r.date_to;
      if(r.as_at && !asAt.value) asAt.value = r.as_at;
      if(r.fy_year && !fyYear.value) fyYear.value = r.fy_year;
      syncPlaceUrl();              // GL / VAT: the period the server settled on, kept in the URL
    } catch(e){ if(seq !== loadSeq) return; err.value = (e.data && e.data.error) || 'Could not load the report'; }
    loading.value = false;
    if(firstLoad){ firstLoad = false; pane(); }
    if(glJournal && !glScrolled) requestAnimationFrame(() => {
      const hl = document.querySelector('.rp-gl-hl');
      if(hl){ glScrolled = true; hl.scrollIntoView({ block: 'center' }); }
    });
    // back on the VAT Report from the ledger: the line you opened, marked and in view
    if(vatFocus && !vatScrolled) requestAnimationFrame(() => {
      const hl = document.querySelector('.v201-hl');
      if(hl){ vatScrolled = true; hl.scrollIntoView({ block: 'center' }); }
    });
  }
  // account-picker reports fetch their (bank-only) account list up front
  if(meta.filter === 'account'){
    api.get('/companies/' + companyId + '/reports/' + code).then(r => { accounts.value = r.accounts || []; }).catch(() => {});
  }
  load();

  const onDebounced = () => { clearTimeout(timer); timer = setTimeout(load, 250); };
  const num = v => html`<span class=${Number(v) < 0 ? 'num-neg' : ''}>${fmtRS(v)}</span>`;
  const numIn = (v, sym) => html`<span class=${Number(v) < 0 ? 'num-neg' : ''}>${fmtMoney(v, sym, true)}</span>`;
  const zero = v => Math.abs(Number(v) || 0) < 0.005;

  // ── v2 quick-date presets (quickRange above — v2's sleek-report-dates.js) ──
  const currentFy = () => fyYear.value || currentFyYear(fyStart());
  // FY lock state (Governance) — a locked year renders a red 🔒 beside the stepper
  const lockedYears = signal({});
  // the company's ACTUAL financial years (earliest activity/seeded → latest of today/activity/seeded)
  // bound the stepper so you can't wander into empty future years that all read the same
  const fyMin = signal(null), fyMax = signal(null);
  // Upper bound = the company's latest real FY — no wandering into empty future years that all read
  // the same. Lower bound is relaxed by PRIOR_YEARS so you CAN step back to view opening / prior
  // years from before trading began (owner 2026-08-26); a past as-at is always valid (reads as zero).
  const PRIOR_YEARS = 10;
  (async () => {
    try { const g = await api.get('/companies/' + companyId + '/governance/years');
          const m = {}, ys = (g.years || []).map(y => Number(y.fy_year)).filter(Boolean);
          (g.years || []).forEach(y => { if(y.locked) m[y.fy_year] = true; });
          lockedYears.value = m;
          if(ys.length){ fyMin.value = Math.min(...ys) - PRIOR_YEARS; fyMax.value = Math.max(...ys); } } catch {}
  })();
  const fmtIso = (y, m, d) => y + '-' + String(m).padStart(2, '0') + '-' + String(d).padStart(2, '0');
  const daysIn = (y, m) => new Date(y, m, 0).getDate();
  function setQuick(preset){
    const [from, to] = quickRange(preset, fyStart(), currentFy());
    if(from && to){ dFrom.value = from; dTo.value = to; load(); }
  }
  const QUICKS = [['this_month', 'reports.this_month', 'This Month'], ['last_month', 'reports.last_month', 'Last Month'], ['bi_monthly', 'reports.bi_monthly', 'Bi-monthly'],
                  ['this_fy', 'reports.this_fy', 'This FY'], ['last_fy', 'reports.last_fy', 'Last FY'],
                  ['ytd', 'common.year_to_date', 'Year to Date'], ['all', 'common.all', 'All']];
  const quickRow = (onPick = setQuick) => html`<div class="rp-quick">
    <span class="rp-quick-lbl">${t('reports.quick_range', 'Quick range:')}</span>
    ${QUICKS.map(([k, kk, lbl]) => html`<button class="btn btn-light btn-sm" @click=${() => onPick(k)}>${t(kk, lbl)}</button>`)}
  </div>`;
  function stepFy(delta){
    const target = (fyYear.value || currentFy()) + delta;
    // stay within the company's real financial years (delta 0 = "FY end" preset, always allowed)
    if(delta > 0 && fyMax.value && target > fyMax.value) return;
    if(delta < 0 && fyMin.value && target < fyMin.value) return;
    fyYear.value = target;
    asAtMode.value = 'fy'; asAt.value = ''; load(); }
  function tbPreset(kind){
    const t = new Date();
    if(kind === 'today') asAt.value = fmtIso(t.getFullYear(), t.getMonth() + 1, t.getDate());
    else { const lm = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      asAt.value = fmtIso(lm.getFullYear(), lm.getMonth() + 1, daysIn(lm.getFullYear(), lm.getMonth() + 1)); }
    asAtMode.value = 'date'; load();
  }
  // TB: Balances (as at) ⇄ Movements (from–to, no opening balances). Entering Movements with no
  // window yet defaults to this month.
  function tbMode(m){
    if(m === 'movement'){
      if(!dFrom.value || !dTo.value){
        const [from, to] = quickRange('this_month', fyStart(), currentFy());
        dFrom.value = from || ''; dTo.value = to || '';
      }
      asAtMode.value = 'movement';
    } else {
      asAtMode.value = 'fy';
    }
    load();
  }
  const tbMoveQuick = k => { asAtMode.value = 'movement'; setQuick(k); };

  // ── GL range pickers — imperative floating dropdowns (focus + arrows survive) ──
  const floatRoot = mountFloatRoot();   // shared layer — ONE document listener app-wide
  const closeFloat = closeFloatRoot;
  let dropEl = null;
  function positionNear(el, anchor, width){
    const r = anchor.getBoundingClientRect();
    el.style.position = 'fixed';
    el.style.left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8)) + 'px';
    el.style.zIndex = 300;
    floatRoot.appendChild(el);
    const h = el.offsetHeight, below = window.innerHeight - r.bottom - 8;
    el.style.top = (below >= h ? r.bottom + 4 : Math.max(8, r.top - h - 4)) + 'px';
  }
  function showRangeDrop(anchor, side){
    const qy = anchor.value.trim().toLowerCase();
    const bounds = side === 'from' ? [['', t('reports.first_account', 'First (lowest) account')]] : [['9999', t('reports.last_account', 'Last (highest) account')]];
    const pool = accounts.value
      .filter(a => !qy || String(a.code).toLowerCase().includes(qy) || String(a.name || '').toLowerCase().includes(qy))
      .slice(0, 20);
    closeFloat();
    dropEl = document.createElement('div');
    dropEl.className = 'cb-dropdown';
    const rows = [...bounds.map(([c, lbl]) => [c, lbl, true, null]),
                  ...pool.map(a => [String(a.code), a.name, false, a.balance])];
    rows.forEach(([codeV, lbl, isBound, bal]) => {
      const item = document.createElement('div');
      item.className = 'cb-drop-item';
      item.innerHTML = isBound ? `<span class="muted">${lbl}</span>`
        : `<span><span class="mono">${gcode(codeV)}</span> ${lbl}</span>` + acctBalTag(bal);
      item.onclick = e => { e.stopPropagation(); pickRange(side, codeV, isBound ? lbl : gcode(codeV) + ' — ' + lbl, anchor); };
      dropEl.appendChild(item);
    });
    if(!rows.length) dropEl.innerHTML = '<div class="cb-drop-item cb-drop-muted">' + t('bank_import.no_matches', 'No matches') + '</div>';
    positionNear(dropEl, anchor, 340);
  }
  function pickRange(side, codeV, label, anchor){
    if(side === 'from') glFromCode = codeV; else glToCode = codeV;
    anchor.value = label;
    closeFloat();
    load();
  }
  // what a range box shows for a code it was opened with (the URL keeps the range — syncPlaceUrl):
  // the same words a pick writes into it
  function rangeLabel(codeV, side){
    if(!codeV) return '';
    if(side === 'to' && codeV === '9999') return t('reports.last_account', 'Last (highest) account');
    const a = accounts.value.find(x => String(x.code) === String(codeV));
    return a ? gcode(a.code) + ' — ' + a.name : codeV;
  }
  function rangeKeydown(e){
    const open = dropEl && floatRoot.contains(dropEl);
    if(!open) return;
    if(e.key === 'Escape'){ closeFloat(); return; }
    if(e.key === 'ArrowDown' || e.key === 'ArrowUp'){ dropdownKeydown(e, dropEl, { wrap: false }); return; }
    if(e.key === 'Enter' || e.key === 'Tab'){
      const current = dropEl.querySelector('.cb-drop-item.highlighted');
      const items = dropEl.querySelectorAll('.cb-drop-item:not(.cb-drop-muted)');
      if(current){ e.preventDefault(); current.click(); return; }
      if(items.length === 1){ e.preventDefault(); items[0].click(); return; }
      if(e.key === 'Enter') e.preventDefault();
      else closeFloat();
    }
  }

  // ── single-account picker — searchable typeahead with balances (GL single + bank statement) ──
  function glSingleLabel(){
    const a = accounts.value.find(x => String(x.id) === String(accountId.value));
    return a ? gcode(a.code) + ' — ' + a.name : '';
  }
  function acctBalTag(bal){
    return (bal === undefined || bal === null) ? ''
      : `<span class="mono cb-drop-bal ${Number(bal) < 0 ? 't-out' : 't-in'}">${fmtRS(bal)}</span>`;
  }
  function showSingleDrop(anchor, showAll){
    const qy = showAll ? '' : anchor.value.trim().toLowerCase();
    const pool = accounts.value
      .filter(a => !qy || String(a.code).toLowerCase().includes(qy) || String(a.name || '').toLowerCase().includes(qy))
      .slice(0, 40);
    closeFloat();
    dropEl = document.createElement('div');
    dropEl.className = 'cb-dropdown';
    pool.forEach(a => {
      const item = document.createElement('div');
      item.className = 'cb-drop-item';
      item.innerHTML = `<span><span class="mono">${gcode(a.code)}</span> ${a.name || ''}</span>` + acctBalTag(a.balance);
      item.onclick = e => { e.stopPropagation(); accountId.value = String(a.id);
        anchor.value = gcode(a.code) + ' — ' + a.name; closeFloat(); load(); };
      dropEl.appendChild(item);
    });
    if(!pool.length) dropEl.innerHTML = '<div class="cb-drop-item cb-drop-muted">' + t('bank_import.no_matches', 'No matches') + '</div>';
    positionNear(dropEl, anchor, 360);
  }
  const acctPicker = (id, ph) => html`<input class="form-control rp-glpick rp-acct" id=${id} autocomplete="off"
    data-float-anchor placeholder=${ph} .value=${glSingleLabel()}
    @input=${e => showSingleDrop(e.target)}
    @focus=${e => { e.target.select(); showSingleDrop(e.target, true); }}
    @keydown=${rangeKeydown}>`;

  // ── CSV export (client-side) ──────────────────────────────────────────────
  function toCsv(rows){ return rows.map(r => r.map(c => {
    const s = c == null ? '' : String(c);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; }).join(',')).join('\n'); }
  function csvRows(){
    const d = data.value; if(!d) return [];
    if(code === 'trial-balance') return [['Code', 'Account', 'Group', 'Debit', 'Credit'],
      ...(d.accounts || []).map(a => [a.code, a.name, a.group, a.debit, a.credit]),
      ['', 'Total', '', d.total_debit, d.total_credit],
      ...(d.profit_loss != null ? [['', d.profit_loss >= 0 ? 'Profit for the year' : 'Loss for the year', '', '', d.profit_loss]] : [])];
    if(code === 'age-analysis') return [['Code', 'Contact', 'Balance', 'Current', '30', '60', '90', '120+'],
      ...(d.contacts || []).map(c2 => [c2.code, c2.name, c2.balance, c2.d_current, c2.d30, c2.d60, c2.d90, c2.d120])];
    if(code === 'general-ledger'){
      if(d.is_range) return [['Account', 'Date', 'Ref', 'Description', 'Source', 'Period', 'Cost', 'Debit', 'Credit', 'Balance'],
        ...(d.groups || []).flatMap(g => [[g.code + ' ' + g.name, '', 'BBF', '', '', '', '', '', '', g.bbf],
          ...g.entries.map(e => [g.code, e.date, e.reference, e.description, e.source, e.period, e.cost_code, e.debit, e.credit, e.balance])])];
      return [['Date', 'Ref', 'Description', 'Source', 'Period', 'Cost', 'Debit', 'Credit', 'Balance'],
        ...(d.entries || []).map(e => [e.date, e.reference, e.description, e.source, e.period, e.cost_code, e.debit, e.credit, e.balance])];
    }
    if(code === 'bank-statement') return [['Date', 'Ref', 'Description', 'Source', 'Debit', 'Credit', 'Balance'],
      ...(d.entries || []).map(e => [e.date, e.reference, e.description, e.source, e.debit, e.credit, e.balance])];
    if(code === 'transactions') return [['Date', 'GL', 'Ref', 'Description', 'Source', 'Debit', 'Credit', 'Tax', 'Cost'],
      ...(d.entries || []).map(e => [e.date, e.gl_code, e.reference, e.description, e.source, e.debit, e.credit, e.tax_code, e.cost_code])];
    if(code === 'accounts') return [['Code', 'Account', 'Group', 'Tax code', 'Type', 'Balance'],
      ...(d.accounts || []).map(a => [a.code, a.name, a.group, a.tax_code,
        (a.root_code || '').toLowerCase() + (a.is_bank ? ' bank' : '') + (a.control_type ? ' ' + a.control_type : ''),
        a.balance])];
    if(code === 'vat' && d.vat201 && vatView.value !== 'codes') return vat201Csv(d.vat201);
    if(code === 'vat') return [['Code', 'Name', 'Rate', 'Entries', 'Exclusive', 'VAT', 'Inclusive'],
      ...(d.vat_summary || []).map(r => [r.code, r.name, (r.rate ? r.rate + '%' : '0%'), r.entry_count, r.total_excl, r.total_tax, r.total_incl]),
      ['', 'TOTAL VAT', '', '', '', d.total_vat, ''],
      [], ['Transaction detail', '', '', '', '', '', ''],
      ['Date', 'Source', 'Reference', 'Description', 'Code', 'Exclusive', 'VAT', 'Inclusive'],
      ...(d.vat_detail || []).map(e => [e.date, e.source, e.reference, e.description, e.tax_code, e.excl, e.vat, e.incl]),
      [], ['', '', 'Output VAT', '', '', d.output_vat, ''], ['', '', 'Input VAT', '', '', d.input_vat, ''],
      ['', '', 'Net VAT', '', '', d.net_vat, '']];
    if(code === 'cost-codes') return [['Code', 'Cost centre', 'Entries', 'Debit', 'Credit', 'Net'],
      ...(d.rows || []).map(r => [r.code, r.name, r.lines, r.total_dr, r.total_cr, r.amount])];
    if(code === 'reconciliation') return [['', ''], ['Bank balance', d.bank_balance], ['Ledger balance', d.ledger_balance],
      // a foreign account's bank side is its own currency, the ledger's the base: no difference of the two
      ['Difference', reconForeign(d) ? '' : d.difference], ['Match rate %', d.match_rate], ['', ''],
      ['Side', 'Date', 'Description', 'Amount', 'Match'],
      ...(d.matched || []).map(m => ['matched', m.bank.date, m.bank.description, m.bank.net, m.match_type]),
      ...(d.bank_only || []).map(b => ['bank only', b.date, b.description, b.net, '']),
      ...(d.ledger_only || []).map(l => ['ledger only', l.date, l.description, l.net, ''])];
    if(code === 'income-statement'){
      const s = d.sections || {};
      const sec = (lbl, arr) => (arr || []).map(x => [lbl, x.code, x.name, x.amount]);
      return [['Section', 'Code', 'Name', 'Amount'],
        ...sec('Sales', s.sales), ...sec('Cost of Sales', s.cos), ...sec('Other Income', s.other_income),
        ...sec('Expenses', s.expenses), ...sec('Tax', s.tax),
        ['Gross Profit', '', '', d.gross_profit], ['Net before tax', '', '', d.net_before_tax],
        ['Net Profit', '', '', d.net_after_tax]];
    }
    if(code === 'balance-sheet'){
      // the payload (and the on-screen report) is GROUPED — asset_groups/liability_groups/
      // equity_groups, each {name, total, accounts:[{code,name,amount}]}. The old CSV read a flat
      // d.assets/d.liabilities/d.equity that the API never sends → an empty export.
      const grp = (lbl, groups) => (groups || []).flatMap(g =>
        (g.accounts || []).map(a => [lbl, a.code, a.name, g.name, a.amount]));
      return [['Section', 'Code', 'Name', 'Group', 'Amount'],
        ...grp('Assets', d.asset_groups),
        ...grp('Liabilities', d.liability_groups),
        ...grp('Equity', d.equity_groups),
        ['Equity', '', 'Current-year earnings', '', d.current_earnings]];
    }
    return [];
  }
  function downloadCsv(){ const rows = csvRows(); if(!rows.length) return;
    const blob = new Blob([toCsv(rows)], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = code + '.csv'; a.click(); URL.revokeObjectURL(a.href); }
  function printReport(){ window.print(); }

  // ── v2 print-bar: real server-rendered PDF / Excel downloads (same params as the JSON) ──
  const dlBusy = signal(''), dlErr = signal('');
  async function downloadFile(fmt){
    dlBusy.value = fmt; dlErr.value = '';
    try {
      const qs = query();
      await api.download('/companies/' + companyId + '/reports/' + code + '/' + fmt + (qs ? '?' + qs : ''),
                         code + (fmt === 'pdf' ? '.pdf' : '.xlsx'));
    } catch(e){ dlErr.value = (e.data && e.data.error) || e.message || t('reports.download_failed', 'Download failed'); }
    dlBusy.value = '';
  }

  // ── Filter bar — ONE reactive block; every signal it needs is read at the top ──
  // …except what changes while a date is typed. Each key that changes the date in a box reloads the report (new
  // `data`), and an as-at date sets `asAtMode` to 'date'. Read here, they rebuilt the bar under the cursor: a
  // new date box in place of the one being typed in, and the rest of the keys went nowhere (owner 2026-10-09:
  // "Typing a date … gives the wrong date"). So the bar reads only whether the Trial Balance shows Movements
  // (its shape); the FY-end button follows the mode by itself; only the Balance Sheet, which has no date box,
  // reads the data; the Cost Code list has its own signal.
  const tbMoving = computed(() => asAtMode.value === 'movement');
  const filterBar = () => {
    const f = meta.filter;
    if(f === 'fy-asat' || f === 'fy'){
      const yr = fyYear.value || '';
      const moving = tbMoving.value;
      const d = f === 'fy' ? data.value : null;
      const fmin = fyMin.value, fmax = fyMax.value;   // read at top (tina4js reactive-slot rule)
      // TB only: Balances (as at a date) ⇄ Movements (activity between two dates, no opening balances)
      const tbToggle = f === 'fy-asat' ? html`<div class="login-kind rp-tb-mode" role="tablist" aria-label="Trial balance mode">
          <button type="button" role="tab" class=${'login-kind-btn' + (!moving ? ' on' : '')}
                  aria-selected=${!moving ? 'true' : 'false'}
                  @click=${() => { if(moving) tbMode('fy'); }}>${t('reports.tb_balances', 'Balances (as at)')}</button>
          <button type="button" role="tab" class=${'login-kind-btn' + (moving ? ' on' : '')}
                  aria-selected=${moving ? 'true' : 'false'}
                  @click=${() => { if(!moving) tbMode('movement'); }}>${t('reports.tb_movements', 'Movements (from – to)')}</button>
        </div>` : '';
      if(f === 'fy-asat' && moving){
        return html`<div class="rp-filters-col">
          ${tbToggle}
          <div class="rp-filters">
            <label class="rp-flabel">${t('reports.from', 'From')}</label>
            <sleek-date .value=${dFrom} @change=${e => { dFrom.value = e.target.value; load(); }}></sleek-date>
            <label class="rp-flabel">${t('reports.to', 'To')}</label>
            <sleek-date .value=${dTo} @change=${e => { dTo.value = e.target.value; load(); }}></sleek-date>
          </div>${quickRow(tbMoveQuick)}</div>`;
      }
      return html`<div class="rp-filters-col">
        ${tbToggle}
        <div class="rp-filters">
          <div class="rp-fywrap"><label class="rp-flabel">${t('reports.financial_year', 'Financial Year')}</label>
            <div class="rp-fynav">
              <button class="btn btn-light btn-sm" ?disabled=${!!fmin && !!yr && yr <= fmin} @click=${() => stepFy(-1)} aria-label="Previous FY">‹</button>
              <span class="rp-fyyear mono">${yr || '…'}${yr && lockedYears.value[yr]
                ? html` <span class="row-lock row-lock-red" title=${t('reports.fy_locked_hint', 'This financial year is LOCKED (Governance 🏛) — the figures are final')}>🔒</span>` : ''}</span>
              <button class="btn btn-light btn-sm" ?disabled=${!!fmax && !!yr && yr >= fmax} @click=${() => stepFy(1)} aria-label="Next FY">${icon("chevron-right", {size: 14})}</button>
            </div></div>
          ${f === 'fy-asat'
            ? html`<div class="rp-fywrap"><label class="rp-flabel">${t('reports.as_at_date', 'As at date')}</label>
                <sleek-date .value=${asAt} @change=${e => { asAt.value = e.target.value; asAtMode.value = 'date'; load(); }}></sleek-date></div>`
            : html`<div class="rp-fywrap"><label class="rp-flabel">${t('reports.as_at', 'As at')}</label>
                <span class="rp-asat-ro mono">${d && d.as_at ? dmy(d.as_at) : '…'}</span></div>`}
        </div>
        ${f === 'fy-asat'
          ? html`<div class="rp-quick">
              <span class="rp-quick-lbl">${t('reports.as_at_label', 'As at:')}</span>
              <button class="btn btn-light btn-sm" @click=${() => tbPreset('today')}>${t('reports.today_ytd', 'Today (YTD)')}</button>
              <button class="btn btn-light btn-sm" @click=${() => tbPreset('last_month')}>${t('reports.end_last_month', 'End of last month')}</button>
              <button class=${() => 'btn btn-sm ' + (asAtMode.value === 'fy' ? 'btn-primary' : 'btn-light')} @click=${() => stepFy(0)}>${t('reports.fy_end', 'FY end')}</button>
            </div>`
          : ''}
      </div>`;
    }
    if(f === 'party'){
      const p = party.value, st = ageStatus.value;
      return html`<div class="rp-filters-col">
        <div class="cb-tabs rp-party">
          <button class=${'cb-tab' + (p === '0' ? ' active' : '')} @click=${() => { party.value = '0'; ageBucket.value = ''; pane(); load(); }}>${t('report.bs.debtors', 'Debtors')}</button>
          <button class=${'cb-tab' + (p === '1' ? ' active' : '')} @click=${() => { party.value = '1'; ageBucket.value = ''; pane(); load(); }}>${t('report.bs.creditors', 'Creditors')}</button>
        </div>
        <div class="cb-tabs rp-party">
          ${[['active', t('common.active', 'Active')], ['disabled', t('common.disabled', 'Disabled')], ['all', t('common.all', 'All')]].map(([k, lbl]) => html`
            <button class=${'cb-tab' + (st === k ? ' active' : '')} @click=${() => { ageStatus.value = k; pane(); load(); }}>${lbl}</button>`)}
        </div></div>`;
    }
    if(f === 'account'){
      return html`<div class="rp-filters-col">
        <div class="rp-filters">
          <label class="rp-flabel">${t('common.account', 'Account')}</label>
          ${acctPicker('rpAcctPick', t('reports.acct_search_bank_ph', 'Search accounts… e.g. 9500 or FNB'))}
          <label class="rp-flabel">${t('reports.from', 'From')}</label>
          <sleek-date .value=${dFrom} @change=${e => { dFrom.value = e.target.value; load(); }}></sleek-date>
          <label class="rp-flabel">${t('reports.to', 'To')}</label>
          <sleek-date .value=${dTo} @change=${e => { dTo.value = e.target.value; load(); }}></sleek-date>
        </div>${quickRow()}</div>`;
    }
    if(f === 'gl'){
      const mode = glMode.value;
      return html`<div class="rp-filters-col">
        <div class="cb-tabs rp-party">
          <button class=${'cb-tab' + (mode === 'single' ? ' active' : '')} @click=${() => { glMode.value = 'single'; pane(); load(); }}>${t('reports.single_account', 'Single account')}</button>
          <button class=${'cb-tab' + (mode === 'range' ? ' active' : '')} @click=${() => { glMode.value = 'range'; pane(); load(); }}>${t('reports.account_range', 'Account range')}</button>
        </div>
        <div class="rp-filters">
          ${mode === 'single'
            ? html`<label class="rp-flabel">${t('common.account', 'Account')}</label>
              ${acctPicker('rpGlSingle', t('reports.acct_search_gl_ph', 'Search accounts… e.g. 0200 or Sales'))}`
            : html`<label class="rp-flabel">${t('reports.from_account', 'From account')}</label>
              <input class="form-control rp-glpick" id="rpGlFrom" autocomplete="off" data-float-anchor
                     .value=${rangeLabel(glFromCode, 'from')}
                     placeholder=${t('reports.from_account_ph', 'e.g. 0000 (or type a name)')}
                     @input=${e => showRangeDrop(e.target, 'from')}
                     @focus=${e => showRangeDrop(e.target, 'from')}
                     @keydown=${rangeKeydown}>
              <label class="rp-flabel">${t('reports.to_account', 'To account')}</label>
              <input class="form-control rp-glpick" id="rpGlTo" autocomplete="off" data-float-anchor
                     .value=${rangeLabel(glToCode, 'to')}
                     placeholder=${t('reports.to_account_ph', 'e.g. 9999 (or type a name)')}
                     @input=${e => showRangeDrop(e.target, 'to')}
                     @focus=${e => showRangeDrop(e.target, 'to')}
                     @keydown=${rangeKeydown}>`}
          <label class="rp-flabel">${t('reports.from', 'From')}</label>
          <sleek-date .value=${dFrom} @change=${e => { dFrom.value = e.target.value; load(); }}></sleek-date>
          <label class="rp-flabel">${t('reports.to', 'To')}</label>
          <sleek-date .value=${dTo} @change=${e => { dTo.value = e.target.value; load(); }}></sleek-date>
        </div>${quickRow()}</div>`;
    }
    if(f === 'costcode'){
      const ccs = costCentres.value;
      return html`<div class="rp-filters-col">
        <div class="rp-filters">
          <label class="rp-flabel">${t('common.cost_code', 'Cost code')}</label>
          <select class="form-control rp-acct" .value=${ccId} @change=${e => { ccId.value = e.target.value; load(); }}>
            <option value="">— ${t('report.cc.all_codes', 'All cost codes')} —</option>
            ${ccs.map(c => html`<option value=${c.id}>${c.code + ' — ' + c.name}</option>`)}
          </select>
          <label class="rp-flabel">${t('reports.from', 'From')}</label>
          <sleek-date .value=${dFrom} @change=${e => { dFrom.value = e.target.value; load(); }}></sleek-date>
          <label class="rp-flabel">${t('reports.to', 'To')}</label>
          <sleek-date .value=${dTo} @change=${e => { dTo.value = e.target.value; load(); }}></sleek-date>
        </div>${quickRow()}</div>`;
    }
    if(f === 'range' || f === 'search'){
      return html`<div class="rp-filters-col">
        <div class="rp-filters">
          <label class="rp-flabel">${t('reports.from', 'From')}</label>
          <sleek-date .value=${dFrom} @change=${e => { dFrom.value = e.target.value; load(); }}></sleek-date>
          <label class="rp-flabel">${t('reports.to', 'To')}</label>
          <sleek-date .value=${dTo} @change=${e => { dTo.value = e.target.value; load(); }}></sleek-date>
          ${f === 'search' ? html`<input class="form-control rp-search" type="search"
            placeholder=${t('reports.txn_search_ph', 'Search reference, GL or text…')} @input=${e => { q.value = e.target.value; onDebounced(); }}>` : ''}
        </div>${quickRow()}</div>`;
    }
    return html``;
  };

  // ── shared row/table snippets (plain values only — called from the one body block) ──
  // A ledger line opens what it came from, as a PDF over the ledger (owner 2026-10-07, the accountant: "it
  // must take you back to the source document"; owner 2026-10-08: "that is what the source document must
  // drill down to" — the PDF that is sent): the confirmed document that posted it — invoice, credit note,
  // supplier invoice, GRV or return (docview.js); a bank line, its bank statement page when the import kept
  // the PDF or image; anything else says there is none. Closing the viewer leaves you on the same line.
  const stmt = statementViewer(companyId);
  const docView = documentViewer(companyId);
  const openSource = e => {
    if(e.document_id) docView.open(e);
    else if(e.statement) stmt.open(e.statement);
    else notice(t('report.gl.no_pdf_or_document', 'No PDF or document available'), 'info');
  };
  const glEntryRow = e => html`<tr class=${'rp-drill' + (glJournal && e.journal_id === glJournal ? ' rp-gl-hl' : '')}
    title=${e.document_id ? t('report.gl.open_source', 'Open the source document')
      : e.statement ? t('report.gl.open_statement', 'Open the bank statement')
      : t('report.gl.no_pdf_or_document', 'No PDF or document available')}
    @click=${() => openSource(e)}>
    <td class="mono">${dmy(e.date)}</td><td class="mono">${e.reference}</td><td>${e.description}</td>
    <td class="rp-dim">${e.source || ''}</td><td class="mono rp-dim">${e.period || ''}</td>
    <td class="mono rp-dim">${e.cost_code || ''}</td>
    <td class="mono text-right">${e.debit ? fmtR(e.debit) : ''}</td>
    <td class="mono text-right">${e.credit ? fmtR(e.credit) : ''}</td>
    <td class="mono text-right">${num(e.balance)}</td></tr>`;
  const GL_HEAD = html`<thead><tr><th>${t('common.date', 'Date')}</th><th>${t('common.ref', 'Ref')}</th><th>${t('common.description', 'Description')}</th><th>${t('common.source', 'Source')}</th>
    <th>${t('common.period', 'Period')}</th><th>${t('common.cost', 'Cost')}</th><th class="text-right">${t('common.debit', 'Debit')}</th><th class="text-right">${t('common.credit', 'Credit')}</th>
    <th class="text-right">${t('common.balance', 'Balance')}</th></tr></thead>`;
  const typeBadges = a => {
    const out = [(a.root_code || '').toLowerCase()];
    return html`${out.join('')}
      ${a.is_bank ? html`<span class="rp-badge rp-badge-bank">${t('report.coa.bank', 'Bank')}</span>` : ''}
      ${a.control_type === 'debtors' ? html`<span class="rp-badge rp-badge-debtor">${t('report.bs.debtors', 'Debtors')}</span>` : ''}
      ${a.control_type === 'creditors' ? html`<span class="rp-badge rp-badge-creditor">${t('report.bs.creditors', 'Creditors')}</span>` : ''}
      ${a.control_type === 'vat' ? html`<span class="rp-badge rp-badge-vat">${t('common.vat', 'VAT')}</span>` : ''}`;
  };

  // ── VAT201 return (South Africa): the layout the accountant files (owner 2026-10-07) — the SARS
  // fields 1–20, every transaction under the field it feeds, then the period's lines not included.
  // Plain values only: it renders inside the one body block.
  // Laid out as a document, like the accountant's return: plain figures (no currency symbol, no
  // monospace), light rules, the field number in bold; every field closes with its Total row — 0.00 when
  // it has no lines (owner 2026-10-08).
  const V201_MEASURE = { gross: ['report.vat.gross', 'Gross'], net: ['report.vat.net', 'Net'], tax: ['common.tax', 'Tax'] };
  const amt = v => html`<span class=${Number(v) < 0 ? 'num-neg' : ''}>${Number(v || 0).toLocaleString('en-US',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>`;
  const v201Head = () => html`<thead><tr><th>${t('common.date', 'Date')}</th><th>${t('common.account', 'Account')}</th>
      <th>${t('common.reference', 'Reference')}</th>
      <th>${t('common.description', 'Description')}</th><th>${t('report.vat.tax_rate', 'Tax rate')}</th>
      <th class="v201-num">${t('report.vat.gross', 'Gross')}</th><th class="v201-num">${t('report.vat.net', 'Net')}</th>
      <th class="v201-num">${t('common.tax', 'Tax')}</th></tr></thead>`;
  // A line opens the General Ledger of its account on that entry — the first of the accountant's "two
  // clicks" to the source document (owner 2026-10-07). An Adjustment line dated before the period widens
  // the ledger's window back to its date.
  const v201Drill = x => {
    const d = data.value || {};
    if(!x.account_id) return;
    const from = x.date && x.date < d.date_from ? x.date : d.date_from;
    syncPlaceUrl(x.journal_id);           // ← from the ledger comes back to this line
    navigate('/co/' + companyId + '/reports/general-ledger?account=' + x.account_id + '&from=' + from
      + '&to=' + d.date_to + '&journal=' + x.journal_id);
  };
  const v201Row = x => html`<tr class=${'v201-drill' + (vatFocus && x.journal_id === vatFocus ? ' v201-hl' : '')}
      title=${t('report.vat.open_ledger', 'Open this entry in the General Ledger')}
      @click=${() => v201Drill(x)}>
      <td class="v201-date">${dmy(x.date)}${x.adjustment ? html`<span class="v201-tag">${t('report.vat.adjustment', 'Adjustment')}</span>` : ''}</td>
      <td>${x.account_code ? gcode(x.account_code) + ' - ' : ''}${x.account_name}${x.unmapped
        ? html`<span class="v201-tag v201-tag-warn">${t('report.vat.not_mapped', 'Not mapped')}</span>` : ''}</td>
      <td>${x.reference}</td><td>${x.description}</td>
      <td class="v201-rate">${x.tax_rate}</td>
      <td class="v201-num">${amt(x.gross)}</td><td class="v201-num">${amt(x.net)}</td><td class="v201-num">${amt(x.tax)}</td></tr>`;
  const vat201View = (v, d) => {
    const period = t('report.vat.for_period', 'For the period') + ' ' + vatLongDate(d.date_from) + ' '
      + t('report.vat.period_to', 'to') + ' ' + vatLongDate(d.date_to);
    // the field number in a small box, as the SARS form prints it (owner 2026-10-08: not "Field 1")
    const fbox = n => html`<span class="v201-fbox" title=${t('report.vat.field', 'Field') + ' ' + n}>${n}</span>`;
    const fieldRows = grp => (v.fields || []).filter(f => f.group === grp).map(f => html`<tr class=${f.kind === 'total' ? 'v201-total' : ''}>
        <td class="v201-fno">${fbox(f.field)}</td><td>${f.label}</td>
        <td class="v201-num">${amt(f.value)}</td></tr>`);
    // one table under ONE column header — "SARS Reference" over the numbers (Andre 2026-10-08) — so the
    // header lines up with every row; each group is a body led by its label
    const fieldGroup = (grp, key, label) => html`<tbody><tr class="v201-grp"><th colspan="3" scope="rowgroup">${t(key, label)}</th></tr>
      ${fieldRows(grp)}</tbody>`;
    const owing = Number(v.payable) >= 0;
    const unmapped = v.unmapped_codes || [];
    return html`<div class="v201">
      <div class="v201-paper">
        <div class="v201-top">
          <div class="v201-title">${t('report.vat.return_title', 'VAT Return')}</div>
          <div class="v201-top-right">
            <span class="v201-meta">${t('report.vat.file_by', 'File by')} ${vatLongDate(v.file_by)}</span>
            <span class=${'v201-status v201-status-' + v.status}>${v.status === 'filed'
              ? t('report.vat.filed', 'Filed') + (v.filed_at ? ' ' + dmy(v.filed_at) : '') : t('report.vat.draft', 'Draft')}</span>
          </div>
        </div>
        <div class="v201-co">${co().name || ''}</div>
        <div class="v201-period">${period}</div>
        ${v.vat_no ? html`<div class="v201-vatno">${t('report.vat.vat_number', 'VAT Number')} ${v.vat_no}</div>` : ''}
        <div class="v201-owing">
          <span>${owing ? t('report.vat.amount_owing', 'Total amount owing') : t('report.vat.refund_due', 'Total refund due')}</span>
          <span>${amt(Math.abs(Number(v.payable) || 0))}</span></div>
        ${unmapped.length ? html`<div class="row-err v201-warn">${t('report.vat.unmapped', 'These tax codes have no VAT201 field, so their lines are not on the return (see Transactions not included):')}
          ${unmapped.map(c => c.code + ' ' + c.name).join(', ')}</div>` : ''}
        <table class="v201-fields"><thead><tr><th class="v201-fno">${t('report.vat.sars_reference', 'SARS Reference')}</th>
          <th>${t('common.description', 'Description')}</th><th class="v201-num">${t('common.amount', 'Amount')}</th></tr></thead>
          ${fieldGroup('output', 'report.vat.output_group', 'Output tax and imported services')}
          ${fieldGroup('input', 'report.vat.input_group', 'Input tax')}
          ${fieldGroup('result', 'report.vat.result_group', 'VAT payable or refundable')}</table>
      </div>
      <div class="v201-paper v201-paper-wide">
        <div class="v201-title v201-title-sm">${t('report.vat.by_field', 'Transactions by field number')}</div>
        <div class="v201-period">${period}</div>
        ${v.adjustments ? html`<div class="rp-tabnote">${t('report.vat.adjustments_note', 'An Adjustment line is dated in an earlier VAT period whose filed return did not include it — it rolls forward into this return.')}</div>` : ''}
        <div class="table-wrap"><table class="v201-tx">${v201Head()}<tbody>
          ${(v.sections || []).map(s => html`
            <tr class="v201-sec"><td colspan="5">${fbox(s.field)}${s.label}</td>
              <td colspan="3" class="v201-num">${t(V201_MEASURE[s.measure][0], V201_MEASURE[s.measure][1])} ${amt(s.headline)}</td></tr>
            ${s.rows.map(v201Row)}
            <tr class="v201-total"><td colspan="5">${t('common.total', 'Total')}</td>
              <td class="v201-num">${amt(s.gross)}</td><td class="v201-num">${amt(s.net)}</td>
              <td class="v201-num">${amt(s.tax)}</td></tr>`)}
        </tbody></table></div>
      </div>
      <div class="v201-paper v201-paper-wide">
        <div class="v201-title v201-title-sm">${t('report.vat.not_included', 'Transactions not included')}</div>
        <div class="table-wrap"><table class="v201-tx">${v201Head()}<tbody>
          ${(v.not_included || []).length ? v.not_included.map(v201Row)
            : html`<tr><td colspan="8" class="muted">${t('report.vat.none_left_out', 'Every line in this period is on the return.')}</td></tr>`}
        </tbody></table></div>
        ${v.rows_capped ? html`<div class="rp-tabnote">${t('report.vat.capped', 'Only the first 5,000 lines of each list are shown.')}</div>` : ''}
      </div>
    </div>`;
  };

  // ── the ONE reactive body — reads every signal it uses at the top ─────────
  const body = () => {
    const isLoading = loading.value, error = err.value, d = data.value;
    const bucket = ageBucket.value, vTab = vatTab.value, rTab = reconTab.value, vView = vatView.value;
    if(isLoading) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(error) return html`<div class="muted-block">${error}</div>`;
    if(!d) return html`<div class="muted-block">${t('reports.no_data', 'No data.')}</div>`;

    if(code === 'trial-balance'){
      // "At report-date rate" toggle: swap the historical base amount on FOREIGN bank rows
      // (a[i].native present) for native.base_at_rate. Base-currency rows never change.
      // Totals recompute from the swapped values so the TB still balances. All derived values
      // read atRate.value INSIDE lambdas so tina4-js signal reactivity re-renders them on toggle
      // (an outer-scope let-const would capture atRate.value at initial render only).
      const baseCode = (d.company && d.company.currency_code) || '';
      const foreignCount = (d.accounts || []).filter(a => a.native).length;
      const rowsFor = () => (d.accounts || []).map(a => {
        const useAtRate = atRate.value && foreignCount > 0 && a.native && a.native.base_at_rate != null;
        const b = useAtRate ? a.native.base_at_rate : null;
        const r = useAtRate ? (b >= 0 ? { debit: b, credit: 0 } : { debit: 0, credit: -b })
                            : { debit: a.debit, credit: a.credit };
        return { a, r };
      });
      const totals = () => {
        const rs = rowsFor();
        const td = rs.reduce((s, x) => s + (x.r.debit || 0), 0);
        const tc = rs.reduce((s, x) => s + (x.r.credit || 0), 0);
        const diff = Math.round((td - tc) * 100) / 100;
        return { td, tc, diff, bal: Math.abs(diff) < 0.005 };
      };
      return html`<div class="rp-cards">
        <div class="rp-kpi"><span class="rp-kpi-lbl">${t('report.tb.total_debit', 'Total Debit')}</span><span class="rp-kpi-val mono">${() => fmtR(totals().td)}</span></div>
        <div class="rp-kpi"><span class="rp-kpi-lbl">${t('report.tb.total_credit', 'Total Credit')}</span><span class="rp-kpi-val mono">${() => fmtR(totals().tc)}</span></div>
        <div class="rp-kpi"><span class="rp-kpi-lbl">${t('report.tb.difference', 'Difference')}</span>
          <span class=${() => 'rp-kpi-val mono ' + (totals().bal ? 'rp-ok-t' : 'rp-bad-t')}>${() => fmtRS(totals().diff)}</span></div>
        ${d.profit_loss != null ? html`<div class="rp-kpi"><span class="rp-kpi-lbl">${d.mode === 'movement'
            ? (d.profit_loss >= 0 ? t('report.tb.profit_period', 'Profit for the period') : t('report.tb.loss_period', 'Loss for the period'))
            : (d.profit_loss >= 0 ? t('report.tb.profit', 'Profit for the year') : t('report.tb.loss', 'Loss for the year'))}</span>
          <span class=${'rp-kpi-val mono ' + (d.profit_loss >= 0 ? 'rp-ok-t' : 'rp-bad-t')}>${fmtRS(d.profit_loss)}</span></div>` : ''}
      </div>
      ${d.mode === 'movement' ? html`<div class="rp-mode-hint muted rp-tb-window">${t('report.tb.movement_hint', 'Movements only from {from} to {to} — no opening balances.')
          .replace('{from}', dmy(d.date_from)).replace('{to}', dmy(d.date_to))}</div>` : ''}
      ${foreignCount > 0 ? html`
      <div class="rp-mode-row">
        <div class="login-kind" role="tablist" aria-label="Balance display mode">
          <button type="button" role="tab" class=${() => 'login-kind-btn' + (!atRate.value ? ' on' : '')}
                  aria-selected=${() => !atRate.value ? 'true' : 'false'}
                  @click=${() => { atRate.value = false; syncTbUrl(); }}>${t('report.tb.as_booked', 'As booked')}</button>
          <button type="button" role="tab" class=${() => 'login-kind-btn' + (atRate.value ? ' on' : '')}
                  aria-selected=${() => atRate.value ? 'true' : 'false'}
                  @click=${() => { atRate.value = true; syncTbUrl(); }}>${t('report.tb.at_rate', 'At report-date rate')}</button>
        </div>
        <div class="rp-mode-hint muted">${() => atRate.value
            ? t('report.tb.at_rate_hint', 'Foreign bank rows shown in') + ' ' + baseCode + ' ' + t('report.tb.at_rate_hint2', 'at the report-date rate. Display only — no journal is posted.')
            : t('report.tb.as_booked_hint', 'Foreign bank rows shown at the rate they were posted at (historical).')}</div>
      </div>` : ''}
      <div class="table-wrap"><table class="cb-table rp-table">
        <thead><tr><th>${t('common.code', 'Code')}</th><th>${t('common.account', 'Account')}</th><th>${t('common.group', 'Group')}</th><th class="text-right">${t('common.debit', 'Debit')}</th><th class="text-right">${t('common.credit', 'Credit')}</th></tr></thead>
        <tbody>${() => rowsFor().map(({ a, r }) => html`<tr class=${(a.id || a.account_id) ? 'rp-drill' : ''}
          title=${(a.id || a.account_id) ? t('reports.drill_hint', 'Open the General Ledger for this account') : ''}
          @click=${() => glDrill(a.id || a.account_id)}>
          <td class="mono">${gcode(a.code)}</td>
          <td>${a.name}${a.native ? html`<div class="rp-fx-sub muted">
              ${a.native.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })} ${a.native.currency}${a.native.rate ? html` · @ ${a.native.rate.toFixed(4)} on ${a.native.rate_date}` : ''}
            </div>` : ''}</td>
          <td class="rp-dim">${a.group}</td>
          <td class="mono text-right tabular-nums">${r.debit ? fmtR(r.debit) : ''}</td>
          <td class="mono text-right tabular-nums">${r.credit ? fmtR(r.credit) : ''}</td></tr>`)}</tbody>
        <tfoot><tr class="rp-total"><td></td><td class="fw-700">${t('common.total', 'Total')}</td><td></td>
          <td class="mono text-right fw-700 tabular-nums">${() => fmtR(totals().td)}</td>
          <td class="mono text-right fw-700 tabular-nums">${() => fmtR(totals().tc)}</td></tr></tfoot>
      </table></div>
      <div class=${() => 'rp-note ' + (totals().bal ? 'rp-ok' : 'rp-bad')}>${() => totals().bal ? t('reports.in_balance', 'In balance.') : t('reports.out_of_balance', 'Out of balance by') + ' ' + fmtR(totals().diff)}</div>`;
    }

    if(code === 'income-statement'){
      const s = d.sections || { sales: [], cos: [], other_income: [], expenses: [], tax: [] };
      const st = d.section_totals || {};
      const accLine = x => html`<div class=${'rp-is-line' + ((x.id || x.account_id) ? ' rp-drill' : '')}
        title=${(x.id || x.account_id) ? t('reports.drill_hint', 'Open the General Ledger for this account') : ''}
        @click=${() => glDrill(x.id || x.account_id)}>
        <span class="rp-is-label">${x.name}</span>
        <span class="rp-is-amt mono">${fmtRS(x.amount)}</span><span class="rp-is-total"></span></div>`;
      const hasCos = s.cos.length > 0, hasTax = s.tax.length > 0, hasOther = s.other_income.length > 0;
      return html`<div class="rp-is">
        <div class="rp-is-head"><div>
          <div class="rp-is-company">${(d.company && d.company.name) || ''}</div>
          <div class="rp-is-title">${t('reports.is_title', 'INCOME STATEMENT')} : ${dmy(d.date_from)} ${t('reports.to_word', 'to')} ${dmy(d.date_to)}</div>
        </div></div>
        <div class="rp-is-sec"><div class="rp-is-sectitle">${t('reports.sales_section', 'SALES')}</div>
          ${s.sales.map(accLine)}
          <div class="rp-is-line rp-is-grptotal"><span class="rp-is-label"></span><span class="rp-is-amt"></span>
            <span class="rp-is-total mono rp-underline">${fmtRS(st.sales || 0)}</span></div></div>
        ${hasCos ? html`<div class="rp-is-sec"><div class="rp-is-sectitle">${t('reports.cos_section', 'COST OF SALES')}</div>
          ${s.cos.map(accLine)}
          <div class="rp-is-line rp-is-grptotal"><span class="rp-is-label"></span>
            <span class="rp-is-amt mono rp-underline">${fmtRS(st.cos || 0)}</span>
            <span class="rp-is-total mono rp-underline">${fmtRS(st.cos || 0)}</span></div></div>
        <div class="rp-is-sec"><div class="rp-is-line rp-is-sub"><span class="rp-is-label fw-700">${t('reports.gross_profit_section', 'GROSS PROFIT / (LOSS)')}</span>
          <span class="rp-is-amt"></span><span class=${'rp-is-total mono fw-700 ' + totalTone(d.gross_profit)}>${fmtRS(d.gross_profit)}</span></div></div>` : ''}
        ${hasOther ? html`<div class="rp-is-sec"><div class="rp-is-sectitle">${t('reports.other_income_section', 'OTHER INCOME')}</div>
          ${s.other_income.map(accLine)}
          <div class="rp-is-line rp-is-grptotal"><span class="rp-is-label"></span>
            <span class="rp-is-amt mono rp-underline">${fmtRS(st.other_income || 0)}</span>
            <span class="rp-is-total mono">${fmtRS(d.subtotal_after_other)}</span></div></div>` : ''}
        <div class="rp-is-sec"><div class="rp-is-sectitle">${t('reports.expenses_section', 'EXPENSES')}</div>
          ${s.expenses.map(accLine)}
          <div class="rp-is-line rp-is-grptotal"><span class="rp-is-label"></span>
            <span class="rp-is-amt mono rp-underline">${fmtRS(st.expenses || 0)}</span>
            <span class="rp-is-total mono rp-underline">${fmtRS(st.expenses || 0)}</span></div></div>
        ${hasTax ? html`<div class="rp-is-sec"><div class="rp-is-line rp-is-sub">
            <span class="rp-is-label fw-700">${t('reports.net_before_tax_section', 'NET PROFIT / (LOSS) BEFORE TAX')}</span><span class="rp-is-amt"></span>
            <span class=${'rp-is-total mono fw-700 ' + totalTone(d.net_before_tax)}>${fmtRS(d.net_before_tax)}</span></div></div>
          <div class="rp-is-sec"><div class="rp-is-sectitle">${t('reports.tax_section', 'TAX')}</div>${s.tax.map(accLine)}</div>` : ''}
        <div class="rp-is-sec rp-is-final"><div class="rp-is-line">
          <span class="rp-is-label fw-700">${t('reports.net_profit_section', 'NET PROFIT / (LOSS)')}</span><span class="rp-is-amt"></span>
          <span class=${'rp-is-total mono fw-700 rp-dblunderline ' + totalTone(d.net_after_tax)}>${fmtRS(d.net_after_tax)}</span></div></div>
      </div>`;
    }

    if(code === 'balance-sheet'){
      const bsGroup = g => html`<div class="rp-bs-group">
        <div class="rp-bs-grouphead"><span>${g.name}</span><span class="mono">${fmtRS(g.total)}</span></div>
        ${g.accounts.filter(a => !zero(a.amount)).map(a => html`<div class=${'rp-bs-row' + ((a.id || a.account_id) ? ' rp-drill' : '')}
          title=${(a.id || a.account_id) ? t('reports.drill_hint', 'Open the General Ledger for this account') : ''}
          @click=${() => glDrill(a.id || a.account_id)}>
          <span class="rp-bs-code mono">${gcode(a.code)}</span>
          <span class="rp-bs-desc">${a.name}
            ${a.is_bank ? html`<span class="rp-badge rp-badge-bank">${t('report.bs.bank', 'Bank')}</span>` : ''}
            ${a.control_type === 'debtors' ? html`<span class="rp-badge rp-badge-debtor">${t('report.bs.debtors', 'Debtors')}</span>` : ''}
            ${a.control_type === 'creditors' ? html`<span class="rp-badge rp-badge-creditor">${t('report.bs.creditors', 'Creditors')}</span>` : ''}</span>
          <span class="rp-bs-amt mono">${fmtRS(a.amount)}</span></div>`)}</div>`;
      return html`<div class="rp-cards">
        <div class="rp-kpi"><span class="rp-kpi-lbl">${t('report.bs.total_assets', 'Total Assets')}</span><span class="rp-kpi-val mono">${fmtRS(d.total_assets)}</span></div>
        <div class="rp-kpi"><span class="rp-kpi-lbl">${t('report.bs.liabilities_equity', 'Liabilities + Equity')}</span><span class="rp-kpi-val mono">${fmtRS(d.total_liabilities_equity)}</span></div>
        <div class="rp-kpi"><span class="rp-kpi-lbl">${t('report.bs.difference', 'Difference')}</span>
          <span class=${'rp-kpi-val mono ' + (zero(d.difference) ? 'rp-ok-t' : 'rp-bad-t')}>${fmtRS(d.difference)}</span></div>
      </div>
      <div class="rp-bs-cols">
        <div class="rp-bs-side"><div class="rp-bs-sidehead">${t('report.bs.assets', 'Assets')}</div>
          ${(d.asset_groups || []).map(bsGroup)}
          ${!(d.asset_groups || []).length ? html`<div class="muted rp-bs-empty">${t('reports.no_assets', 'No assets.')}</div>` : ''}
          <div class="rp-bs-total"><span>${t('report.bs.total_assets', 'Total Assets')}</span><span class="mono">${fmtRS(d.total_assets)}</span></div>
        </div>
        <div class="rp-bs-side"><div class="rp-bs-sidehead">${t('reports.liabilities_equity_head', 'Liabilities & Equity')}</div>
          ${(d.liability_groups || []).map(bsGroup)}
          <div class="rp-bs-subtotal"><span>${t('report.bs.total_liabilities', 'Total Liabilities')}</span><span class="mono">${fmtRS(d.total_liabilities)}</span></div>
          ${(d.equity_groups || []).map(bsGroup)}
          <div class="rp-bs-row rp-bs-retained"><span class="rp-bs-code"></span><span class="rp-bs-desc"><em>${t('reports.current_earnings', 'Current-year earnings')}</em></span>
            <span class=${'rp-bs-amt mono ' + totalTone(d.current_earnings)}>${fmtRS(d.current_earnings)}</span></div>
          <div class="rp-bs-subtotal"><span>${t('report.bs.total_equity', 'Total Equity')}</span><span class=${'mono ' + totalTone(d.total_equity)}>${fmtRS(d.total_equity)}</span></div>
          <div class="rp-bs-total"><span>${t('report.bs.liabilities_equity', 'Liabilities + Equity')}</span><span class="mono">${fmtRS(d.total_liabilities_equity)}</span></div>
        </div>
      </div>
      <div class=${'rp-note ' + (zero(d.difference) ? 'rp-ok' : 'rp-bad')}>${zero(d.difference) ? t('reports.bs_balanced', 'Balanced (Assets = Liabilities + Equity).') : t('reports.out_by', 'Out by') + ' ' + fmtRS(d.difference)}</div>`;
    }

    if(code === 'age-analysis'){
      const tot = d.totals || {};
      const BUCKETS = [['', t('common.total', 'Total'), tot.balance], ['d_current', t('common.current', 'Current'), tot.d_current], ['d30', t('common.30_days', '30 Days'), tot.d30],
                       ['d60', t('common.60_days', '60 Days'), tot.d60], ['d90', t('common.90_days', '90 Days'), tot.d90], ['d120', t('reports.age_120_days', '120+ Days'), tot.d120]];
      let list = d.contacts || [];
      if(bucket) list = [...list].filter(c => !zero(c[bucket])).sort((a, b) => Math.abs(b[bucket]) - Math.abs(a[bucket]));
      return html`<div class="rp-cards rp-age-cards">
        ${BUCKETS.map(([k, lbl, val]) => html`<div class=${'rp-kpi rp-age-kpi' + (bucket === k ? ' rp-age-on' : '')}
             title=${t('report.age.click_filter', 'Click to filter')} @click=${() => { ageBucket.value = k; }}>
          <span class="rp-kpi-lbl">${lbl}</span>
          <span class=${'rp-kpi-val mono' + ((k === 'd60' && val > 0) ? ' rp-warn-t' : '') + (((k === 'd90' || k === 'd120') && val > 0) ? ' rp-bad-t' : '')}>${fmtRS(val)}</span>
        </div>`)}</div>
      ${bucket ? html`<div class="rp-age-filterlbl">${t('reports.age_filter_pre', 'Showing contacts with a')} ${(BUCKETS.find(b => b[0] === bucket) || [])[1]} ${t('reports.age_filter_post', 'balance —')}
        <button class="btn btn-link rp-age-clear" @click=${() => { ageBucket.value = ''; }}>${t('reports.show_all', 'show all')}</button></div>` : ''}
      <div class="table-wrap"><table class="cb-table rp-table">
        <thead><tr><th>${t('common.code', 'Code')}</th><th>${t('common.name', 'Name')}</th><th class="text-right">${t('common.balance', 'Balance')}</th><th class="text-right">${t('common.current', 'Current')}</th>
          <th class="text-right">${t('common.30_days', '30 Days')}</th><th class="text-right">${t('common.60_days', '60 Days')}</th><th class="text-right">${t('common.90_days', '90 Days')}</th><th class="text-right">${t('common.120_plus', '120+')}</th></tr></thead>
        <tbody>${list.length ? list.map(c => html`<tr class="rp-age-row" @click=${() => navigate('/co/' + companyId + '/contacts/' + c.id)}>
          <td class="mono">${c.code || ''}${c.is_active === false ? html` <span class="rp-badge rp-badge-off">${t('common.disabled', 'Disabled')}</span>` : ''}</td>
          <td>${c.name}</td>
          <td class="mono text-right fw-700">${num(c.balance)}</td>
          <td class="mono text-right">${fmtR(c.d_current)}</td>
          <td class="mono text-right">${fmtR(c.d30)}</td>
          <td class=${'mono text-right' + (c.d60 > 0 ? ' rp-warn-t' : '')}>${fmtR(c.d60)}</td>
          <td class=${'mono text-right' + (c.d90 > 0 ? ' rp-bad-t' : '')}>${fmtR(c.d90)}</td>
          <td class=${'mono text-right' + (c.d120 > 0 ? ' rp-bad-t' : '')}>${fmtR(c.d120)}</td></tr>`)
          : html`<tr><td colspan="8" class="muted">${t('reports.nothing_outstanding', 'Nothing outstanding.')}</td></tr>`}</tbody>
        <tfoot><tr class="rp-total"><td class="fw-700" colspan="2">${t('common.totals', 'Totals')}</td>
          <td class="mono text-right fw-700">${num(tot.balance)}</td>
          <td class="mono text-right fw-700">${fmtR(tot.d_current)}</td><td class="mono text-right fw-700">${fmtR(tot.d30)}</td>
          <td class="mono text-right fw-700">${fmtR(tot.d60)}</td><td class="mono text-right fw-700">${fmtR(tot.d90)}</td>
          <td class="mono text-right fw-700">${fmtR(tot.d120)}</td></tr></tfoot>
      </table></div>`;
    }

    if(code === 'general-ledger' && d.is_range){
      // ONE table for the whole range (a <tbody> per account) so every group shares the SAME
      // column geometry. Separate per-group tables auto-sized their columns independently, so
      // Date/Description/Debit/Credit/Balance drifted out of line down the page.
      return html`<div class="rp-gl-head">${d.account.name} · ${dmy(d.date_from)} – ${dmy(d.date_to)}</div>
        ${(d.groups || []).length ? html`<div class="table-wrap"><table class="cb-table rp-table">${GL_HEAD}
          ${(d.groups || []).map(g => html`<tbody class="rp-gl-tbody">
            <tr class="rp-gl-grouprow"><td colspan="9"><span class="mono">${gcode(g.code)}</span> ${g.name}</td></tr>
            <tr class="rp-bbf"><td class="mono">${dmy(d.date_from)}</td><td></td><td class="fw-700">${t('report.gl.balance_brought_forward', 'Balance brought forward')}</td>
              <td></td><td></td><td></td><td></td><td></td><td class="mono text-right fw-700">${num(g.bbf)}</td></tr>
            ${g.entries.map(glEntryRow)}
            <tr class="rp-total"><td colspan="8" class="fw-700">${t('reports.subtotal', 'Subtotal')} — ${g.name}</td>
              <td class="mono text-right fw-700">${num(g.subtotal)}</td></tr>
          </tbody>`)}
        </table></div>`
        : html`<div class="muted-block">${t('reports.no_movements_range', 'No movements in this range.')}</div>`}`;
    }

    if(code === 'general-ledger' || code === 'bank-statement'){
      if(!d.account) return html`<div class="muted-block">${code === 'bank-statement' ? t('reports.pick_bank_statement', 'Pick a bank account to view its statement.') : t('reports.pick_gl_ledger', 'Pick an account to view its ledger.')}</div>`;
      const cards = code === 'bank-statement' ? html`<div class="rp-cards">
        <div class="rp-kpi"><span class="rp-kpi-lbl">${t('reports.opening', 'Opening')}</span><span class=${'rp-kpi-val mono ' + totalTone(d.opening_balance)}>${fmtRS(d.opening_balance)}</span></div>
        <div class="rp-kpi"><span class="rp-kpi-lbl">${t('report.bank_statement.money_in', 'Money In')}</span><span class="rp-kpi-val mono rp-ok-t">${fmtR(d.total_debits)}</span></div>
        <div class="rp-kpi"><span class="rp-kpi-lbl">${t('report.bank_statement.money_out', 'Money Out')}</span><span class="rp-kpi-val mono rp-bad-t">${fmtR(d.total_credits)}</span></div>
        <div class="rp-kpi"><span class="rp-kpi-lbl">${t('reports.closing', 'Closing')}</span>
          <span class=${'rp-kpi-val mono ' + totalTone(d.closing_balance)}>${fmtRS(d.closing_balance)}</span></div>
      </div>` : html``;
      return html`${cards}
        <div class="rp-gl-head">${gcode(d.account.code)} — ${d.account.name} · ${dmy(d.date_from)} – ${dmy(d.date_to)}</div>
        <div class="table-wrap"><table class="cb-table rp-table">${GL_HEAD}
        <tbody>
          <tr class="rp-bbf"><td class="mono">${dmy(d.date_from)}</td><td></td><td class="fw-700">${t('report.gl.opening_balance', 'Opening balance')}</td>
            <td></td><td></td><td></td><td></td><td></td><td class="mono text-right fw-700">${num(d.opening_balance)}</td></tr>
          ${(d.entries || []).length ? (d.entries || []).map(glEntryRow)
            : html`<tr><td colspan="9" class="muted">${t('reports.no_movements_period', 'No movements in this period.')}</td></tr>`}
        </tbody>
        <tfoot><tr class="rp-total"><td colspan="8" class="fw-700">${t('report.bank_statement.closing', 'Closing balance')}</td>
          <td class="mono text-right fw-700">${num(d.closing_balance)}</td></tr></tfoot>
      </table></div>`;
    }

    if(code === 'transactions'){
      const ts = txnSort.value;   // read the signal at the TOP of this block (tina4js nested-slot rule)
      // SERVER-side sort (reload) so it orders the full set before the 500-row cap, not just the loaded rows
      const onSort = k => { txnSort.value = nextSort(txnSort.value, k, (k === 'date' || k === 'debit' || k === 'credit') ? 'desc' : 'asc'); load(); };
      return html`<div class="table-wrap"><table class="cb-table rp-table">
        <thead><tr>${sortTh(t('common.date', 'Date'), 'date', ts, onSort)}${sortTh(t('common.gl', 'GL'), 'gl_code', ts, onSort)}${sortTh(t('common.ref', 'Ref'), 'reference', ts, onSort)}${sortTh(t('common.description', 'Description'), 'description', ts, onSort)}${sortTh(t('common.source', 'Source'), 'source', ts, onSort)}${sortTh(t('common.debit', 'Debit'), 'debit', ts, onSort, { align: 'right' })}${sortTh(t('common.credit', 'Credit'), 'credit', ts, onSort, { align: 'right' })}${sortTh(t('common.tax', 'Tax'), 'tax_code', ts, onSort)}${sortTh(t('common.cost', 'Cost'), 'cost_code', ts, onSort)}</tr></thead>
        <tbody>${(d.entries || []).length ? (d.entries || []).map(e => html`<tr>
          <td class="mono">${dmy(e.date)}</td>
          <td class="mono" title=${e.gl_name}>${gcode(e.gl_code)}</td>
          <td class="mono">${e.reference}</td><td>${e.description}</td><td class="rp-dim">${e.source}</td>
          <td class="mono text-right">${e.debit ? fmtR(e.debit) : ''}</td>
          <td class="mono text-right">${e.credit ? fmtR(e.credit) : ''}</td>
          <td class="mono rp-dim">${e.tax_code}</td><td class="mono rp-dim">${e.cost_code}</td></tr>`)
          : html`<tr><td colspan="9" class="muted">${t('reports.no_txns_period', 'No transactions in this period.')}</td></tr>`}</tbody>
      </table></div>`;
    }

    if(code === 'accounts'){
      const cs = accSort.value;   // read the signal at the TOP of this block (tina4js nested-slot rule)
      const onSort = k => { accSort.value = nextSort(accSort.value, k, k === 'balance' ? 'desc' : 'asc'); pane(); };
      // one value accessor per column key — Balance sorts numerically, Type by its derived label
      const accGet = (a, k) => k === 'balance' ? Number(a.balance || 0)
        : k === 'type' ? ((a.root_code || '').toLowerCase() + (a.is_bank ? ' bank' : '') + (a.control_type ? ' ' + a.control_type : ''))
        : k === 'name' ? a.name : k === 'group' ? a.group : k === 'tax_code' ? a.tax_code : a.code;
      const rows = sortRows(d.accounts || [], cs, accGet);
      const accountRow = a => {
        const drillable = !!a.id && !!a.is_postable;
        const href = drillable ? glHref(a.id) : '';
        const title = t('report.coa.open_ledger', 'Open General Ledger') + ' — ' + gcode(a.code) + ' ' + a.name;
        const cell = value => drillable ? html`<a href=${href} title=${title}
          @click=${e => {
            if(e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button > 0) return;
            // Tina4's history-mode anchor interceptor uses pathname alone; keep this drill's query.
            e.preventDefault(); e.stopPropagation(); glDrill(a.id);
          }}>${value}</a>` : value;
        return html`<tr class=${drillable ? 'rp-drill' : ''}
          @click=${e => {
            // Native links retain keyboard, Ctrl/Meta-click and open-in-new-tab behavior.
            if(drillable && !e.target.closest('a') && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey)
              glDrill(a.id);
          }}>
          <td class="mono">${cell(gcode(a.code))}</td><td>${cell(a.name)}</td>
          <td class="rp-dim">${a.group}</td><td class="mono rp-dim">${a.tax_code}</td>
          <td>${typeBadges(a)}</td><td class="mono text-right">${cell(num(a.balance))}</td></tr>`;
      };
      return html`${d.date_from && d.date_to ? html`
        <div class="rp-gl-head">${t('report.coa.financial_year', 'Financial year')}: ${dmy(d.date_from)} – ${dmy(d.date_to)}</div>
        <div class="rp-tabnote">${t('report.coa.posted_year_end', 'Posted balances through financial year-end. Future-dated posted entries are included; unposted journals are excluded.')}</div>` : ''}
        <div class="table-wrap"><table class="cb-table rp-table">
        <thead><tr>${sortTh(t('common.code', 'Code'), 'code', cs, onSort)}${sortTh(t('common.description', 'Description'), 'name', cs, onSort)}${sortTh(t('common.group', 'Group'), 'group', cs, onSort)}${sortTh(t('common.tax_code', 'Tax code'), 'tax_code', cs, onSort)}${sortTh(t('common.type', 'Type'), 'type', cs, onSort)}${sortTh(t('common.balance', 'Balance'), 'balance', cs, onSort, { align: 'right' })}</tr></thead>
        <tbody>${rows.map(accountRow)}</tbody>
      </table></div>`;
    }

    if(code === 'vat'){
      // South Africa: the VAT201 return (as the accountant files it) ⇄ the by-tax-code view
      const viewTabs = d.vat201 ? html`<div class="cb-tabs rp-party v201-views">
          <button class=${'cb-tab' + (vView !== 'codes' ? ' active' : '')} @click=${() => { vatView.value = 'vat201'; pane(); syncPlaceUrl(); }}>${t('report.vat.vat201_view', 'VAT201 Return')}</button>
          <button class=${'cb-tab' + (vView === 'codes' ? ' active' : '')} @click=${() => { vatView.value = 'codes'; pane(); syncPlaceUrl(); }}>${t('report.vat.codes_view', 'By Tax Code')}</button>
        </div>` : '';
      if(d.vat201 && vView !== 'codes') return html`${viewTabs}${vat201View(d.vat201, d)}`;
      const groupTable = g => {
        const sExcl = g.rows.reduce((s, r) => s + (r.total_excl || 0), 0);
        const sIncl = g.rows.reduce((s, r) => s + (r.total_incl || 0), 0);
        return html`<div class="rp-gl-head">${g.label}</div>
        <div class="table-wrap"><table class="cb-table rp-table">
        <thead><tr><th>${t('common.code', 'Code')}</th><th>${t('common.description', 'Description')}</th><th class="text-right">${t('reports.excl', 'Exclusive')}</th>
          <th class="text-right">${t('common.vat', 'VAT')}</th><th class="text-right">${t('reports.incl', 'Inclusive')}</th><th class="text-right">${t('reports.entries_col', 'Entries')}</th></tr></thead>
        <tbody>${g.rows.map(r => html`<tr>
          <td class="mono">${r.code}</td><td>${r.name}</td>
          <td class="mono text-right">${num(r.total_excl)}</td><td class="mono text-right">${num(r.total_tax)}</td>
          <td class="mono text-right">${num(r.total_incl)}</td><td class="mono text-right">${r.entry_count}</td></tr>`)}</tbody>
        <tfoot><tr class="rp-total"><td></td><td class="fw-700">${t('reports.subtotal', 'Subtotal')}</td>
          <td class="mono text-right fw-700">${num(sExcl)}</td><td class="mono text-right fw-700">${num(g.subtotal)}</td>
          <td class="mono text-right fw-700">${num(sIncl)}</td><td></td></tr></tfoot>
      </table></div>`;
      };
      return html`${viewTabs}<div class="rp-cards">
        <div class="rp-kpi"><span class="rp-kpi-lbl">${t('reports.output_vat', 'Output VAT')}</span><span class="rp-kpi-val mono">${fmtRS(d.output_vat)}</span></div>
        <div class="rp-kpi"><span class="rp-kpi-lbl">${t('reports.input_vat', 'Input VAT')}</span><span class="rp-kpi-val mono">${fmtRS(d.input_vat)}</span></div>
        <div class="rp-kpi"><span class="rp-kpi-lbl">${d.net_vat >= 0 ? t('reports.net_vat_payable', 'Net VAT payable') : t('reports.net_vat_refundable', 'Net VAT refundable')}</span>
          <span class=${'rp-kpi-val mono ' + (d.net_vat >= 0 ? 'rp-bad-t' : 'rp-ok-t')}>${fmtR(Math.abs(d.net_vat))}</span></div>
      </div>
      ${(d.vat_groups || []).length ? (d.vat_groups || []).map(groupTable)
        : html`<div class="muted-block">No VAT entries in this period.</div>`}
      ${(d.vat_detail || []).length ? (() => {
        // Detail split three ways (owner 2026-09-21: "All, Documents, Entries"): all taxed lines,
        // the actual-document ones (invoices/credit notes/GRV/returns), and the cashbook/manual
        // entries. Each renders the SAME view — grouped by the trading LEDGER account in chart sort
        // order, Excl/VAT/Incl per line + a per-account subtotal.
        const det = d.vat_detail || [];
        const docs = det.filter(e => e.is_document);
        const ents = det.filter(e => !e.is_document);
        const rows = vTab === 'documents' ? docs : vTab === 'entries' ? ents : det;
        const groupedDetail = list => {
          const groups = []; let cur = null;
          list.forEach(e => {
            if(!cur || cur.gl_code !== e.gl_code){ cur = { gl_code: e.gl_code, gl_name: e.gl_name, rows: [], excl: 0, vat: 0, incl: 0 }; groups.push(cur); }
            cur.rows.push(e); cur.excl += e.excl || 0; cur.vat += e.vat || 0; cur.incl += e.incl || 0;
          });
          return html`<div class="table-wrap"><table class="cb-table rp-table">
            <thead><tr><th>${t('common.date', 'Date')}</th><th>${t('common.ref', 'Ref')}</th><th>${t('common.description', 'Description')}</th><th>${t('common.tax', 'Tax')}</th>
              <th class="text-right">${t('reports.excl', 'Excl.')}</th><th class="text-right">${t('common.vat', 'VAT')}</th><th class="text-right">${t('reports.incl', 'Incl.')}</th></tr></thead>
            <tbody>${groups.length ? groups.map(g => html`
              <tr class="rp-group-hd"><td colspan="7" class="fw-700"><span class="mono">${gcode(g.gl_code)}</span> ${g.gl_name}</td></tr>
              ${g.rows.map(e => html`<tr>
                <td class="mono">${dmy(e.date)}</td><td class="mono">${e.reference}</td><td>${e.description}</td><td class="mono rp-dim">${e.tax_code}</td>
                <td class="mono text-right">${num(e.excl)}</td><td class="mono text-right">${num(e.vat)}</td><td class="mono text-right">${num(e.incl)}</td></tr>`)}
              <tr class="rp-total"><td colspan="4" class="text-right fw-700">${gcode(g.gl_code)} ${t('reports.subtotal', 'subtotal')}</td>
                <td class="mono text-right fw-700">${num(g.excl)}</td><td class="mono text-right fw-700">${num(g.vat)}</td><td class="mono text-right fw-700">${num(g.incl)}</td></tr>`)
              : html`<tr><td colspan="7" class="muted">${t('reports.no_vat_entries', 'No entries in this view.')}</td></tr>`}</tbody>
          </table></div>`;
        };
        return html`
          <div class="cb-tabs rp-party rp-vat-tabs">
            <button class=${'cb-tab' + (vTab === 'all' ? ' active' : '')} @click=${() => { vatTab.value = 'all'; pane(); }}>${t('report.vat.all', 'All')} (${det.length})</button>
            <button class=${'cb-tab' + (vTab === 'documents' ? ' active' : '')} @click=${() => { vatTab.value = 'documents'; pane(); }}>${t('report.vat.documents', 'Documents')} (${docs.length})</button>
            <button class=${'cb-tab' + (vTab === 'entries' ? ' active' : '')} @click=${() => { vatTab.value = 'entries'; pane(); }}>${t('report.vat.entries', 'Entries')} (${ents.length})</button>
          </div>
          ${groupedDetail(rows)}`;
      })() : ''}`;
    }

    if(code === 'cost-codes'){
      return html`<div class="table-wrap"><table class="cb-table rp-table">
        <thead><tr><th>${t('common.code', 'Code')}</th><th>${t('reports.cost_centre', 'Cost centre')}</th><th class="text-right">${t('reports.entries_col', 'Entries')}</th>
          <th class="text-right">${t('common.debit', 'Debit')}</th><th class="text-right">${t('common.credit', 'Credit')}</th><th class="text-right">${t('common.net', 'Net')}</th></tr></thead>
        <tbody>${(d.rows || []).length ? (d.rows || []).map(r => html`<tr>
          <td class="mono">${r.code}</td><td>${r.name}</td><td class="mono text-right">${r.lines}</td>
          <td class="mono text-right">${fmtR(r.total_dr)}</td><td class="mono text-right">${fmtR(r.total_cr)}</td>
          <td class="mono text-right">${num(r.amount)}</td></tr>`)
          : html`<tr><td colspan="6" class="muted">${t('reports.no_cost_movements', 'No cost-coded movements in this period.')}</td></tr>`}</tbody>
        <tfoot><tr class="rp-total"><td></td><td class="fw-700">${t('common.total', 'Total')}</td><td></td>
          <td class="mono text-right fw-700">${fmtR(d.total_dr)}</td>
          <td class="mono text-right fw-700">${fmtR(d.total_cr)}</td>
          <td class=${'mono text-right fw-700 ' + totalTone(d.total)}>${num(d.total)}</td></tr></tfoot>
      </table></div>`;
    }

    if(code === 'reconciliation'){
      if(!d.account) return html`<div class="muted-block">${t('reports.pick_reconcile', 'Pick a bank account to reconcile.')}</div>`;
      const matchRateCls = d.match_rate >= 90 ? 'rp-ok-t' : d.match_rate >= 50 ? 'rp-warn-t' : 'rp-bad-t';
      // the bank side carries the ACCOUNT's mark, the ledger side the base mark; a foreign account
      // has no Difference (it would subtract a base figure from one in another currency)
      const bSym = reconSym(d), foreign = reconForeign(d);
      const bankRow = b => html`<tr><td class="mono">${dmy(b.date)}</td><td>${b.description}</td>
        <td class="mono">${b.reference || ''}</td><td class="mono text-right">${numIn(b.net, bSym)}</td></tr>`;
      const ledgerRow = l => html`<tr><td class="mono">${dmy(l.date)}</td><td>${l.description}</td>
        <td class="rp-dim">${l.source || ''}</td><td class="mono text-right">${num(l.net)}</td></tr>`;
      return html`<div class="rp-gl-head">${gcode(d.account.code)} — ${d.account.name} · ${dmy(d.date_from)} – ${dmy(d.date_to)}</div>
      <div class="rp-cards">
        <div class="rp-kpi"><span class="rp-kpi-lbl">${t('report.reconciliation.bank_balance', 'Bank balance')}</span><span class=${'rp-kpi-val mono ' + totalTone(d.bank_balance)}>${fmtMoney(d.bank_balance, bSym, true)}</span></div>
        <div class="rp-kpi"><span class="rp-kpi-lbl">${t('report.reconciliation.ledger_balance', 'Ledger balance')}</span><span class=${'rp-kpi-val mono ' + totalTone(d.ledger_balance)}>${fmtRS(d.ledger_balance)}</span></div>
        <div class="rp-kpi"><span class="rp-kpi-lbl">${t('report.reconciliation.difference', 'Difference')}</span>
          ${foreign ? html`<span class="rp-kpi-val mono" title=${t('report.reconciliation.no_diff_foreign', 'The bank balance is in the account’s currency and the ledger balance in the company’s currency, so they are not subtracted.')}>—</span>`
            : html`<span class=${'rp-kpi-val mono ' + (zero(d.difference) ? 'rp-ok-t' : 'rp-bad-t')}>${fmtRS(d.difference)}</span>`}</div>
        <div class="rp-kpi"><span class="rp-kpi-lbl">${t('report.reconciliation.match_rate', 'Match rate')}</span>
          <span class=${'rp-kpi-val mono ' + matchRateCls}>${d.match_rate}%</span></div>
      </div>
      <div class="cb-tabs rp-party">
        <button class=${'cb-tab' + (rTab === 'matched' ? ' active' : '')} @click=${() => { reconTab.value = 'matched'; pane(); }}>${t('report.reconciliation.matched', 'Matched')} <span class="rp-count">${(d.matched || []).length}</span></button>
        <button class=${'cb-tab' + (rTab === 'bank' ? ' active' : '')} @click=${() => { reconTab.value = 'bank'; pane(); }}>${t('report.reconciliation.bank_only', 'Bank only')} <span class="rp-count rp-count-warn">${(d.bank_only || []).length}</span></button>
        <button class=${'cb-tab' + (rTab === 'ledger' ? ' active' : '')} @click=${() => { reconTab.value = 'ledger'; pane(); }}>${t('report.reconciliation.ledger_only', 'Ledger only')} <span class="rp-count rp-count-info">${(d.ledger_only || []).length}</span></button>
      </div>
      ${rTab === 'matched' ? html`<div class="table-wrap"><table class="cb-table rp-table">
        <thead><tr><th>${t('common.date', 'Date')}</th><th>${t('reports.desc_bank', 'Description (bank)')}</th><th class="text-right">${t('report.reconciliation.amount_match', 'Amount')}</th>
          <th>${t('reports.desc_ledger', 'Description (ledger)')}</th><th class="text-right">${t('report.reconciliation.amount_match', 'Amount')}</th><th>${t('report.reconciliation.match_type', 'Match')}</th></tr></thead>
        <tbody>${(d.matched || []).length ? (d.matched || []).map(m => html`<tr>
          <td class="mono">${dmy(m.bank.date)}</td><td>${m.bank.description}</td>
          <td class="mono text-right">${numIn(m.bank.net, bSym)}</td><td>${m.ledger.description}</td>
          <td class="mono text-right">${num(m.ledger.net)}</td>
          <td><span class=${'rp-match rp-match-' + m.match_type}>${m.match_type === 'exact' ? t('reports.exact_match', 'Exact match') : t('reports.amount_match_label', 'Amount match')}</span></td></tr>`)
          : html`<tr><td colspan="6" class="muted">${t('reports.no_matches_period', 'No matches in this period.')}</td></tr>`}</tbody></table></div>`
      : rTab === 'bank' ? html`<div class="rp-tabnote">${t('reports.recon_bank_note', 'On the bank statement but not in the ledger — still to be allocated.')}</div>
        <div class="table-wrap"><table class="cb-table rp-table">
        <thead><tr><th>${t('common.date', 'Date')}</th><th>${t('common.description', 'Description')}</th><th>${t('common.ref', 'Ref')}</th><th class="text-right">${t('report.reconciliation.amount_match', 'Amount')}</th></tr></thead>
        <tbody>${(d.bank_only || []).length ? (d.bank_only || []).map(bankRow)
          : html`<tr><td colspan="4" class="muted">${t('reports.recon_bank_empty', 'Nothing outstanding on the bank side.')}</td></tr>`}</tbody></table></div>`
      : html`<div class="rp-tabnote">${t('reports.recon_ledger_note', 'In the ledger but not on the bank statement — cheques not presented, or captured directly.')}</div>
        <div class="table-wrap"><table class="cb-table rp-table">
        <thead><tr><th>${t('common.date', 'Date')}</th><th>${t('common.description', 'Description')}</th><th>${t('common.source', 'Source')}</th><th class="text-right">${t('report.reconciliation.amount_match', 'Amount')}</th></tr></thead>
        <tbody>${(d.ledger_only || []).length ? (d.ledger_only || []).map(ledgerRow)
          : html`<tr><td colspan="4" class="muted">${t('reports.recon_ledger_empty', 'Nothing outstanding on the ledger side.')}</td></tr>`}</tbody></table></div>`}`;
    }
    return html`<div class="muted-block">${t('reports.not_available', "This report isn't available yet.")}</div>`;
  };

  const content = html`<div class="page page-wide">
    <div class="page-head with-action rp-noprint"><div>
      ${backChip({ path: '/co/' + companyId + '/reports', label: t('reports.back', 'Reports') })}
      <h1 class="page-title">${() => t(meta.t, meta.name)}</h1></div>
      <div class="rp-actions">
        ${() => { const b = dlBusy.value; return html`
          <button class="btn btn-light" ?disabled=${b === 'pdf'} @click=${() => downloadFile('pdf')} title=${t('reports.download_pdf', 'Download as PDF')}>${b === 'pdf' ? '…' : html`${icon('download', {size: 14})} PDF`}</button>
          <button class="btn btn-light" ?disabled=${b === 'excel'} @click=${() => downloadFile('excel')} title=${t('reports.download_excel', 'Download as Excel')}>${b === 'excel' ? '…' : html`${icon('download', {size: 14})} Excel`}</button>`; }}
        <button class="btn btn-light" @click=${downloadCsv} title=${t('reports.download_csv', 'Download CSV')}>${icon('download', {size: 14})} CSV</button>
        <button class="btn btn-light" @click=${printReport} title=${t('common.print', 'Print')}>${icon('printer', {size: 14})} ${t('common.print', 'Print')}</button>
      </div></div>
    ${() => dlErr.value ? html`<div class="row-err rp-noprint">${dlErr.value}</div>` : ''}
    <div class="rp-printhead">${() => t(meta.t, meta.name) + (data.value && data.value.company && data.value.company.name ? ' — ' + data.value.company.name : '')}</div>
    ${filterBar}
    <div class=${pane.cls('')}>${body}</div>
    ${() => stmt.view()}
    ${() => docView.view()}
  </div>`;
  return tenantShell(companyId, t(meta.t, meta.name), content);
}
